import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { activity } from '../utils/activityLogger';
import { debugStep } from '../utils/debugStep';
import { useFacetedFilters } from '../hooks/useFacetedFilters';
// Multi-tab fix: cliente activo en sessionStorage (per-tab).
import {
  getStoredSelectedClientRaw,
  setStoredSelectedClientRaw,
} from '../utils/clientStorage';
import {
  FileText,
  BarChart3,
  GitCompare,
  User,
  LogOut,
  MoreVertical,
  Table,
  AlertCircle,
  X,
  Database,
  Grid3x3,
  Settings,
  Building2,
  RefreshCw,
  Layers,
  TrendingUp,
  Download
} from 'lucide-react';
import DataTable from './DataTable';
import GridView from './GridView';
import StatisticsPanel from './StatisticsPanel';
import Comparar from './Comparar';
import SuperAdminPanel from './SuperAdminPanel';
import { resetEditClientCache } from './admin/ClientsManagement';
import ErrorBoundary from './ErrorBoundary';
import Catalogado from './catalogado/Catalogado';
import { SKUsManagement } from './skus';
import TrendsPanel from './TrendsPanel';
import ExportPanel from './ExportPanel';
import { getTrendsSnapshot, applyTrendsSnapshot } from './trends/trendsFilterBridge';
import { getExportSnapshot, applyExportSnapshot } from './export/exportFilterBridge';
import { AlertsTab } from './alerts';
import { useSavedFilters, normalizeTomaScope } from '../hooks/useSavedFilters';
import SaveFilterModal from './SaveFilterModal';
import PositionGrid from './PositionGrid';
import UnifiedFilterPanel from './UnifiedFilterPanel';
import { SkeletonStatCard, SkeletonDataTable } from './skeletons';

interface TomaInfo {
  tableName: string;
  tomaId?: number;
  fecha?: string;
  displayName: string;
}

// Interfaz para los filtros compartidos
interface SharedFilters {
  global_search?: string;
  marca?: string;
  sitio?: string;
  categoria?: string;
  price_from?: string;
  price_to?: string;
  price_column?: string;
  [key: string]: any;
}

const Dashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentUser, userData, logout } = useAuth();
  const { selectedClient, isInAdminMode, isLoadingClients, setSelectedClient, setIsInAdminMode } = useClient();

  // columnConfig se hidrata desde la respuesta de /tables/:t/schema (público a
  // cualquier usuario autenticado, no requiere admin como /clients/:id/column-config).
  // Usado para opt-in features per cliente — ej. filtro de subcategoría.
  const [tableColumnConfig, setTableColumnConfig] = useState<any>(null);
  // Filter enabled: el checkbox "Permitir filtrar por X" (core.X.filterable) es el master switch.
  // El filterLayout sólo puede refinar la posición/visibilidad DENTRO de lo que el master permite.
  // Efectivo = core.filterable AND (layout no lo deshabilita; si no hay layout, no veta).
  const filterLayout = tableColumnConfig?.filterLayout;
  const isFilterEnabledInLayout = (key: string) => {
    if (!filterLayout?.rows) return undefined; // no layout → no veta
    for (const row of filterLayout.rows) {
      const field = row.fields?.find((f: any) => f.key === key);
      if (field) return field.enabled;
    }
    return false; // layout exists but field not in it → disabled
  };
  const subcategoryFilterEnabled = !!(tableColumnConfig?.core?.subcategory?.filterable) && (isFilterEnabledInLayout('subcategory') ?? true);
  const segmentoFilterEnabled = !!(tableColumnConfig?.core?.segmento?.filterable) && (isFilterEnabledInLayout('segment') ?? true);

  // Debug log to see selectedClient on every render

  const [tables, setTables] = useState<TomaInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<string>(() => {
    // Intentar recuperar la tabla seleccionada del localStorage
    const savedTable = localStorage.getItem('selectedTable');
    return savedTable || '';
  });
  const [loading, setLoading] = useState(true);
  const [tablesLoaded, setTablesLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'error'>('connecting');
  // TEMPORALMENTE OCULTADO: Cambiado de 'catalogado' a 'estadisticas' - Para reactivar, cambiar de vuelta a 'catalogado'
  const [activeTab, setActiveTab] = useState<'estadisticas' | 'tendencias' | 'publicaciones' | 'exportar' | 'comparar' | 'posicion' | 'catalogado' | 'alertas' | 'admin'>(() => {
    try {
      const validTabs = ['estadisticas', 'tendencias', 'publicaciones', 'exportar', 'comparar', 'posicion', 'catalogado', 'alertas', 'admin'] as const;
      const viewParam = new URLSearchParams(window.location.search).get('view');
      if (viewParam && (validTabs as readonly string[]).includes(viewParam)) {
        return viewParam as typeof validTabs[number];
      }
    } catch (e) {
      // ignore
    }
    return 'estadisticas';
  });

  // Persistir activeTab en URL query param 'view' (preservando otros params).
  // NO sobrescribir si hay query params de transición (editClient) — ese flow
  // navega a admin y ClientsManagement los necesita intactos.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('editClient')) return;
      if (params.get('view') === activeTab) return;
      params.set('view', activeTab);
      const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
      window.history.replaceState(null, '', newUrl);
    } catch (e) {
      // ignore
    }
  }, [activeTab]);

  // Activity tracking (GH #21 / #82): registrar pageview por cambio de sección.
  // La navegación es state-based (no react-router): `activeTab` ES la "ruta".
  // Fire-and-forget, non-fatal.
  useEffect(() => {
    activity.pageview(activeTab);
  }, [activeTab]);
  // Sub-tab para Publicaciones: 'listado' (default) o 'skus'
  const [publicacionesSubTab, setPublicacionesSubTab] = useState<'listado' | 'skus'>('listado');
  const [skuRefreshKey, setSkuRefreshKey] = useState(0);
  
  // Estado para tipo de vista (tabla o grilla) con memoria en localStorage
  const [viewMode, setViewMode] = useState<'table' | 'grid'>(() => {
    try {
      const savedViewMode = localStorage.getItem('userViewPreference');
      return (savedViewMode === 'table' || savedViewMode === 'grid') ? savedViewMode : 'table';
    } catch (error) {
      return 'table';
    }
  });
  
  // Ref para AbortController de loadAllProducts
  const loadAllProductsControllerRef = useRef<AbortController | null>(null);

  // Estado compartido de filtros
  const [sharedFilters, setSharedFilters] = useState<SharedFilters>({});
  
  // Estados de filtros extraídos de DataTable
  const [globalFilter, setGlobalFilter] = useState('');
  // Nuevo estado para manejar operadores por término
  const [searchTermOperators, setSearchTermOperators] = useState<{[key: string]: 'OR' | 'AND' | 'NOT'}>({});
  // Estado para operadores aplicados
  const [appliedSearchTermOperators, setAppliedSearchTermOperators] = useState<{[key: string]: 'OR' | 'AND' | 'NOT'}>({});
  // Estado para resetear el componente de búsqueda
  const [resetTrigger, setResetTrigger] = useState(0);
  // Estados para el nuevo sistema de búsqueda con pills internas (ya no se usan aquí)
  // const [searchInput, setSearchInput] = useState('');
  // const [searchPills, setSearchPills] = useState<Array<{id: string, text: string, operator: 'OR' | 'AND' | 'NOT'}>>([]);
  // const [hasPendingChanges, setHasPendingChanges] = useState(false);
  
  const [marcaFilter, setMarcaFilter] = useState<string[]>([]);
  const [sitioFilter, setSitioFilter] = useState<string[]>([]);
  const [categoriaFilter, setCategoriaFilter] = useState<string[]>([]);
  const [subcategoryFilter, setSubcategoryFilter] = useState<string[]>([]);
  const [appliedSubcategoryFilter, setAppliedSubcategoryFilter] = useState<string[]>([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState<string[]>([]);
  // Meta (counts + total). Shape: { counts: Record<string, number>, total: number }.
  type OptionsMeta = { counts: Record<string, number>; total: number } | null;
  const [subcategoryOptionsMeta, setSubcategoryOptionsMeta] = useState<OptionsMeta>(null);
  const [segmentoFilter, setSegmentoFilter] = useState<string[]>([]);
  const [appliedSegmentoFilter, setAppliedSegmentoFilter] = useState<string[]>([]);
  const [segmentoOptions, setSegmentoOptions] = useState<string[]>([]);
  const [segmentoOptionsMeta, setSegmentoOptionsMeta] = useState<OptionsMeta>(null);
  // Reset subcategory/segmento options when client or selectedTable changes (lazy-loaded on dropdown open)
  useEffect(() => {
    setSubcategoryOptions([]);
    setSubcategoryOptionsMeta(null);
    setSegmentoOptions([]);
    setSegmentoOptionsMeta(null);
  }, [selectedClient?.clientId, selectedTable]);
  // Reset columnConfig snapshot when client changes (se rehidrata en loadTableSchema)
  useEffect(() => {
    setTableColumnConfig(null);
  }, [selectedClient?.clientId]);
  const [priceFromFilter, setPriceFromFilter] = useState('');
  const [priceToFilter, setPriceToFilter] = useState('');
  // Estados para filtros de precio aplicados (solo se actualizan al presionar Filtrar)
  const [appliedPriceFromFilter, setAppliedPriceFromFilter] = useState('');
  const [appliedPriceToFilter, setAppliedPriceToFilter] = useState('');
  
  // Estados para filtros aplicados de marcas, sitios y categorías
  const [appliedMarcaFilter, setAppliedMarcaFilter] = useState<string[]>([]);
  const [appliedSitioFilter, setAppliedSitioFilter] = useState<string[]>([]);
  const [appliedCategoriaFilter, setAppliedCategoriaFilter] = useState<string[]>([]);

  // Pending removals — usuario click X en pill applied. NO modifica applied
  // hasta que click "Filtrar". Habilita el botón Filtrar para que el cambio sea
  // explícito (user request 2026-05-30).
  const [pendingRemoveMarcas, setPendingRemoveMarcas] = useState<string[]>([]);
  const [pendingRemoveSitios, setPendingRemoveSitios] = useState<string[]>([]);
  const [pendingRemoveCategorias, setPendingRemoveCategorias] = useState<string[]>([]);
  const [pendingRemoveSubcategorias, setPendingRemoveSubcategorias] = useState<string[]>([]);
  const [pendingRemoveSegmentos, setPendingRemoveSegmentos] = useState<string[]>([]);

  // Estado para errores de validación de precios
  const [priceValidationErrors, setPriceValidationErrors] = useState<string[]>([]);
  
  // Estados para filtros aplicados de posición y descuentos
  const [appliedMaxPageFilter, setAppliedMaxPageFilter] = useState('');
  const [appliedMaxOrderFilter, setAppliedMaxOrderFilter] = useState('');
  const [appliedDescuentoFilter, setAppliedDescuentoFilter] = useState('all');
  
  // Estados para los nuevos filtros de paginación
  const [maxPageFilter, setMaxPageFilter] = useState('');
  const [maxOrderFilter, setMaxOrderFilter] = useState('');
  
  // Estado para filtro de descuentos
  const [descuentoFilter, setDescuentoFilter] = useState<string>('all'); // 'all', 'with_discount', 'without_discount'

  // Opciones para dropdowns
  const [marcaOptions, setMarcaOptions] = useState<string[]>([]);
  const [sitioOptions, setSitioOptions] = useState<string[]>([]);
  const [categoriaOptions, setCategoriaOptions] = useState<string[]>([]);

  // Meta (counts + total) — feature 2026-05-30: total en placeholder + counts por opción.
  // counts mapea value (incluye '(vacío)') → cantidad. total = buckets distintos.
  // OptionsMeta type ya declarado arriba (junto a subcategoryOptionsMeta).
  const [marcaOptionsMeta, setMarcaOptionsMeta] = useState<OptionsMeta>(null);
  const [sitioOptionsMeta, setSitioOptionsMeta] = useState<OptionsMeta>(null);
  const [categoriaOptionsMeta, setCategoriaOptionsMeta] = useState<OptionsMeta>(null);
  
  // Nombres de columnas
  const [marcaColumnName, setMarcaColumnName] = useState('');
  // Columna REAL de marca en la toma (ej. 'Marca'). Se usa para construir el
  // filtro de /data cuando marcaColumnName es '__tag:MARCA'. FIX Adidas 2026-07-22:
  // el dropdown muestra counts desde facets (toma.Marca) pero el filtro mandaba
  // '__tag:MARCA' → tabla tags congelada en toma_59 → listado 11.259 vs dropdown 150.
  // Alineamos el filtro a la misma fuente que el dropdown (columna toma).
  const [marcaTomaColumn, setMarcaTomaColumn] = useState('');
  const [sitioColumnName, setSitioColumnName] = useState<string>('sitio'); // Default to 'sitio'
  const [categoriaColumnName, setCategoriaColumnName] = useState('');
  const [priceColumnName, setPriceColumnName] = useState('');
  
  // Estados para búsqueda en dropdowns
  
  // Estados para mostrar/ocultar dropdowns
  
  // Estado para mostrar/ocultar filtros
  const [showFilters, setShowFilters] = useState(false);
  
  // Estado para productos en comparación
  const [comparisonProducts, setComparisonProducts] = useState<any[]>([]);
  
  // Estado para todos los productos disponibles (para edición de grupos)
  const [allProducts, setAllProducts] = useState<any[]>([]);
  
  // Estado para el dropdown del usuario
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Estado para modal de filtros guardados
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  

  // Estado para filtro activo
  const [activeFilterName, setActiveFilterName] = useState<string | null>(null);
  const [activeFilterOriginal, setActiveFilterOriginal] = useState<{ [key: string]: any } | null>(null);
  
  // Estado para indicar cuando se están aplicando filtros guardados
  const [isApplyingSavedFilter, setIsApplyingSavedFilter] = useState(false);

  // ---- Faceted search: cross-filter counts for filter dropdowns ----
  // PT-#47 part a — Los counts del dropdown se recalculan PER-CLICK sobre la selección
  // EFECTIVA = (applied ∪ pending) menos pendingRemove. Esto logra dos cosas:
  //  • al tildar/destildar en el dropdown los counts se actualizan en vivo, y
  //  • al Aplicar (pending→applied y pending queda vacío) la selección efectiva NO cambia,
  //    así los facets NO se regeneran/resetean (bug "se resetean todos los dropdowns al
  //    Filtrar"). Antes leía sólo el pending, que al Aplicar caía a [] y disparaba el reset.
  // Scope single-toma (Pubs); Trends es multi-toma y no usa este hook.
  const facetFilters = useMemo(() => {
    const eff = (applied: string[], pending: string[], remove: string[]) => {
      const set = new Set<string>();
      applied.forEach(v => { if (!remove.includes(v)) set.add(v); });
      pending.forEach(v => set.add(v));
      return Array.from(set);
    };
    const f: Record<string, string> = {};
    const brand = eff(appliedMarcaFilter, marcaFilter, pendingRemoveMarcas);
    const site = eff(appliedSitioFilter, sitioFilter, pendingRemoveSitios);
    const category = eff(appliedCategoriaFilter, categoriaFilter, pendingRemoveCategorias);
    const subcategory = eff(appliedSubcategoryFilter, subcategoryFilter, pendingRemoveSubcategorias);
    const segment = eff(appliedSegmentoFilter, segmentoFilter, pendingRemoveSegmentos);
    if (brand.length > 0) f.brand = brand.join('|');
    if (site.length > 0) f.site = site.join('|');
    if (category.length > 0) f.category = category.join('|');
    if (subcategory.length > 0) f.subcategory = subcategory.join('|');
    if (segment.length > 0) f.segment = segment.join('|');
    return f;
  }, [
    appliedMarcaFilter, marcaFilter, pendingRemoveMarcas,
    appliedSitioFilter, sitioFilter, pendingRemoveSitios,
    appliedCategoriaFilter, categoriaFilter, pendingRemoveCategorias,
    appliedSubcategoryFilter, subcategoryFilter, pendingRemoveSubcategorias,
    appliedSegmentoFilter, segmentoFilter, pendingRemoveSegmentos,
  ]);

  // Columna a usar como KEY del filtro de marca en /data. Si el display usa tags
  // ('__tag:MARCA') pero el dropdown/facets cuenta sobre la columna toma, el filtro
  // DEBE usar la columna toma para que listado y navegación coincidan con el dropdown.
  // FIX Adidas 2026-07-22.
  const brandFilterColumn = useMemo(() => {
    if (marcaColumnName && marcaColumnName.startsWith('__tag:')) {
      return marcaTomaColumn || 'Marca';
    }
    return marcaColumnName || 'Marca';
  }, [marcaColumnName, marcaTomaColumn]);

  const facetedEnabled = !!(selectedTable && !isInAdminMode && selectedClient?.clientId !== 'admin-mode');
  const { getOptions: facetGetOptions, getMeta: facetGetMeta, loading: facetsLoading } = useFacetedFilters(
    facetFilters,
    { tableName: selectedTable, enabled: facetedEnabled, debounceMs: 400 }
  );

  // When faceted results arrive, update options/meta for all filter dropdowns.
  // Only skip update when faceted returns 0 real values AND we already have good data
  // (protects against LG where catalog fields are empty but toma columns have data).
  // Cross-filter reductions (fewer options when filters are active) ARE allowed.
  useEffect(() => {
    if (!facetedEnabled || facetsLoading) return;

    const updateIfValid = (
      dim: string,
      currentOpts: string[],
      setOpts: (v: string[]) => void,
      setMeta: (v: any) => void,
    ) => {
      const newOpts = facetGetOptions(dim as any);
      const newMeta = facetGetMeta(dim as any);
      const newReal = newOpts.filter(v => v !== '(vacío)').length;
      const curReal = currentOpts.filter(v => v !== '(vacío)').length;
      // Block only when facets returns 0 real values but we already have data
      // (wrong data source). Allow all other updates including reductions.
      if (newReal === 0 && curReal > 0) return;
      if (newOpts.length > 0) {
        setOpts(newOpts);
        setMeta(newMeta);
      }
    };

    updateIfValid('brand', marcaOptions, setMarcaOptions, setMarcaOptionsMeta);
    updateIfValid('site', sitioOptions, setSitioOptions, setSitioOptionsMeta);
    updateIfValid('category', categoriaOptions, setCategoriaOptions, setCategoriaOptionsMeta);

    if (subcategoryFilterEnabled) {
      updateIfValid('subcategory', subcategoryOptions, setSubcategoryOptions, setSubcategoryOptionsMeta);
    }
    if (segmentoFilterEnabled) {
      updateIfValid('segment', segmentoOptions, setSegmentoOptions, setSegmentoOptionsMeta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facetedEnabled, facetsLoading, facetGetOptions, facetGetMeta, subcategoryFilterEnabled, segmentoFilterEnabled]);

  // Cascading: Cat → Subcat → Seg. Only clear children when the user actively
  // changes a parent filter (not on mount, not when cascading itself clears a child).
  const prevCatRef = useRef(appliedCategoriaFilter.join('|'));
  const prevSubRef = useRef(appliedSubcategoryFilter.join('|'));
  useEffect(() => {
    const current = appliedCategoriaFilter.join('|');
    if (current === prevCatRef.current) return; // no real change
    prevCatRef.current = current;
    // Only clear if user had subcategory/segment selected
    setSubcategoryFilter([]);
    setAppliedSubcategoryFilter(prev => prev.length === 0 ? prev : []);
    setSegmentoFilter([]);
    setAppliedSegmentoFilter(prev => prev.length === 0 ? prev : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCategoriaFilter.join('|')]);

  useEffect(() => {
    const current = appliedSubcategoryFilter.join('|');
    if (current === prevSubRef.current) return;
    prevSubRef.current = current;
    setSegmentoFilter([]);
    setAppliedSegmentoFilter(prev => prev.length === 0 ? prev : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSubcategoryFilter.join('|')]);

  // Guardar tabla seleccionada en localStorage cuando cambie
  useEffect(() => {
    if (selectedTable) {
      localStorage.setItem('selectedTable', selectedTable);
    }
  }, [selectedTable]);

  // Hook para filtros guardados (solo cuando NO estamos en modo admin)
  const {
    savedFilters,
    loading: loadingSavedFilters,
    saveFilter,
    updateFilter,
    deleteFilter,
    compareFilters,
    hasChanges,
    findMatchingFilter
  } = useSavedFilters(selectedTable, !isInAdminMode && selectedClient?.clientId !== 'admin-mode');

  // Filter options are loaded via loadFilterOptions called from loadTableSchema
  // (after schema detection determines the correct column names)
  
  // Cargar productos desde localStorage al iniciar (específico por cliente)
  useEffect(() => {
    if (!selectedClient || isLoadingClients) return;
    
    try {
      // Use client-specific key for comparison products
      const storageKey = `comparisonProducts_${selectedClient.clientId}`;
      const storedProducts = localStorage.getItem(storageKey);
      
      if (storedProducts) {
        const parsedProducts = JSON.parse(storedProducts);
        if (Array.isArray(parsedProducts)) {
          setComparisonProducts(parsedProducts);
        }
      } else {
        // Clean up old global comparison products if they exist
        localStorage.removeItem('comparisonProducts');
        setComparisonProducts([]);
      }
    } catch (error) {
      setComparisonProducts([]);
    }
  }, [selectedClient, isLoadingClients]);

  // Guardar productos en localStorage cuando cambien (específico por cliente)
  useEffect(() => {
    if (!selectedClient) return;
    
    try {
      const storageKey = `comparisonProducts_${selectedClient.clientId}`;
      
      if (comparisonProducts.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(comparisonProducts));
      } else {
        localStorage.removeItem(storageKey); // Limpiar si no hay productos
      }
    } catch (error) {
      // Error silenciado
    }
  }, [comparisonProducts, selectedClient]);
  
  // Funciones para manejar comparación
  const addToComparison = useCallback((product: any) => {
    const id_catalogo = product.id_catalogo || product.ID_CATALOGO;
    
    if (!id_catalogo) {
      return;
    }
    
    // Estrategia de ID único simplificada:
    // 1. Si el producto tiene 'id' (demo/buybox), usarlo como comparisonId
    // 2. Si no tiene 'id' (clientes activos), usar id_catalogo (que es único)
    const uniqueComparisonId = product.id ? String(product.id) : String(id_catalogo);
    
    
    // Usar función de actualización para evitar problemas de estado stale
    setComparisonProducts(prevProducts => {
      // Verificar si ya existe
      const exists = prevProducts.find(p => {
        return p.comparisonId === uniqueComparisonId;
      });
      
      if (!exists) {
        // Agregar información de la tabla de origen
        const newProduct = { 
          ...product, 
          comparisonId: uniqueComparisonId, // Usar el ID único generado
          originalTableName: selectedTable, // Guardar la tabla de origen
          // Preservar información del buybox si existe
          buybox_id: product.buybox_id,
          buybox_winner: product.buybox_winner,
          buyboxVariantsCount: product.buyboxVariantsCount
        };
        return [...prevProducts, newProduct];
      }
      
      return prevProducts; // No cambiar el estado si ya existe
    });
  }, [selectedTable]);
  
  const removeFromComparison = (productId: string) => {
    
    const newComparisonProducts = comparisonProducts.filter(p => {
      // Usar solo comparisonId para la comparación, ya que es único
      const shouldKeep = p.comparisonId !== productId;
      return shouldKeep;
    });
    
    setComparisonProducts(newComparisonProducts);
  };
  
  const clearComparison = () => {
    setComparisonProducts([]);
  };

  // Función para cargar todos los productos disponibles
  const loadAllProducts = async (tableName: string) => {
    if (!tableName) {
      setAllProducts([]);
      return;
    }

    // Cancelar request anterior si existe
    if (loadAllProductsControllerRef.current) {
      loadAllProductsControllerRef.current.abort();
    }

    const controller = new AbortController();
    loadAllProductsControllerRef.current = controller;

    try {
      const response = await api.get(`/api/tables/${tableName}/data?limit=100000&offset=0`, {
        signal: controller.signal
      });

      if (response.data && response.data.data) {
        setAllProducts(response.data.data);
      } else {
        setAllProducts([]);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        setAllProducts([]);
      }
    }
  };

  // Función para hacer logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Error silenciado
    }
  };

  // Función para cambiar vista y guardar en localStorage
  const handleViewModeChange = (newViewMode: 'table' | 'grid') => {
    setViewMode(newViewMode);
    try {
      localStorage.setItem('userViewPreference', newViewMode);
    } catch (error) {
      // Error silenciado
    }
  };

  // Obtener filtros actuales para filtros guardados
  const getCurrentFiltersForSaving = () => {
    const filters: { [key: string]: any } = {};
    
    if (globalFilter) {
      filters['global_search'] = globalFilter;
    }
    
    // Guardar operadores de búsqueda si existen
    if (Object.keys(appliedSearchTermOperators).length > 0) {
      filters['search_operators'] = appliedSearchTermOperators;
    }
    
    // Guardar filtros APLICADOS, no los pendientes
    // Usar nombres de columna genéricos si no están cargados aún
    if (appliedMarcaFilter.length > 0) {
      filters['marca'] = appliedMarcaFilter.join('|');
    }
    if (appliedSitioFilter.length > 0) {
      filters['sitio'] = appliedSitioFilter.join('|');
    }
    if (appliedCategoriaFilter.length > 0) {
      filters['categoria'] = appliedCategoriaFilter.join('|');
    }
    if (appliedSubcategoryFilter.length > 0) {
      filters['subcategory'] = appliedSubcategoryFilter.join('|');
    }
    if (appliedSegmentoFilter.length > 0) {
      filters['segmento'] = appliedSegmentoFilter.join('|');
    }

    if (appliedPriceFromFilter) {
      filters['price_from'] = appliedPriceFromFilter;
    }
    if (appliedPriceToFilter) {
      filters['price_to'] = appliedPriceToFilter;
    }
    
    if (appliedMaxPageFilter) {
      filters['max_page'] = appliedMaxPageFilter;
    }
    if (appliedMaxOrderFilter) {
      filters['max_order'] = appliedMaxOrderFilter;
    }
    
    if (appliedDescuentoFilter !== 'all') {
      filters['descuento_filter'] = appliedDescuentoFilter;
    }

    // PT-#58 — En la pestaña Tendencias, extender el saved filter con los ejes propios de
    // esa vista (nivel de análisis, agrupación, métrica, tipo de precio, acotado temporal,
    // límite de tomas + dimensiones combinables). Snapshot vivo desde el bridge; si la card
    // no está montada, getTrendsSnapshot() devuelve null y no se agrega nada (retrocompatible).
    if (activeTab === 'tendencias') {
      const trends = getTrendsSnapshot();
      if (trends) {
        filters['trends'] = trends;
      }
    }

    // PT-#61 — En la pestaña Exportar, extender el saved filter con los ejes propios de esa
    // vista (5 dimensiones combinables + alcance temporal). Snapshot vivo desde el bridge; si
    // el panel no está montado, getExportSnapshot() devuelve null y no se agrega nada.
    if (activeTab === 'exportar') {
      const exp = getExportSnapshot();
      if (exp) {
        filters['export'] = exp;
      }
    }

    return filters;
  };

  // Cargar filtros desde un filtro guardado
  const loadFiltersFromSaved = async (filters: { [key: string]: any }, filterName?: string) => {
    // Indicar que estamos aplicando filtros guardados
    setIsApplyingSavedFilter(true);
    
    try {
      // Usar setTimeout para agrupar todas las actualizaciones de estado
      await new Promise(resolve => {
        setTimeout(() => {
          // Limpiar todos los filtros primero
          setGlobalFilter('');
          setSearchTermOperators({});
          setAppliedSearchTermOperators({});
          
          // Aplicar búsqueda global y operadores de búsqueda como pendientes
          setGlobalFilter(filters.global_search || '');
          if (filters.search_operators) {
            setSearchTermOperators(filters.search_operators);
          }
          
          // Resetear el componente de búsqueda para que cargue los nuevos términos
          setResetTrigger(prev => prev + 1);
          
          // Inicializar arrays vacíos para filtros que no se encuentren
          // (subcategory/segmento arrancan vacíos → si el saved filter viejo
          //  no los trae, quedan reseteados y matan los "filtros fantasma")
          let newMarcaFilter: string[] = [];
          let newSitioFilter: string[] = [];
          let newCategoriaFilter: string[] = [];
          let newSubcategoryFilter: string[] = [];
          let newSegmentoFilter: string[] = [];
          let newMarcaColumnName = marcaColumnName;
          let newSitioColumnName = sitioColumnName;
          let newCategoriaColumnName = categoriaColumnName;
          
          // Buscar filtros por nombres de columna conocidos o detectar automáticamente
          Object.entries(filters).forEach(([key, value]) => {
            if (key === 'global_search' || key === 'price_from' || key === 'price_to' ||
                key === 'price_column' || key === 'max_page' || key === 'max_order' ||
                key === 'descuento_filter' || key === 'subcategory' || key === 'segmento') {
              return; // Ya procesados arriba o abajo
            }
            
            // Detectar tipo de filtro por nombre de columna exacto o similar
            const keyLower = key.toLowerCase();
            
            // Manejar tanto nombres genéricos como específicos de columnas
            if (key === 'marca' || key === 'Marca' || keyLower === 'marca' || keyLower.includes('marca') || keyLower === 'brand') {
              const marcaArray = typeof value === 'string' ? value.split('|') : (Array.isArray(value) ? value : []);
              newMarcaFilter = marcaArray;
              // Si es el nombre genérico 'marca', usar el nombre de columna actual si está disponible
              newMarcaColumnName = (key === 'marca' && marcaColumnName) ? marcaColumnName : key;
            } else if (key === 'sitio' || keyLower === 'sitio' || keyLower === 'site' || key === 'url') {
              const sitioArray = typeof value === 'string' ? value.split('|') : (Array.isArray(value) ? value : []);
              newSitioFilter = sitioArray;
              // Si es el nombre genérico 'sitio', usar el nombre de columna actual si está disponible
              newSitioColumnName = (key === 'sitio' && sitioColumnName) ? sitioColumnName : key;
            } else if (key === 'categoria' || key === 'Cat' || keyLower === 'categoria' || keyLower === 'category' || keyLower === 'cat') {
              const categoriaArray = typeof value === 'string' ? value.split('|') : (Array.isArray(value) ? value : []);
              newCategoriaFilter = categoriaArray;
              // Si es el nombre genérico 'categoria', usar el nombre de columna actual si está disponible
              newCategoriaColumnName = (key === 'categoria' && categoriaColumnName) ? categoriaColumnName : key;
            }
          });

          // Subcategory/segmento (opt-in per cliente). Si el saved filter los trae,
          // se aplican; si NO (filtro viejo), quedan en [] → reset explícito.
          if (typeof filters.subcategory === 'string' && filters.subcategory.length > 0) {
            newSubcategoryFilter = filters.subcategory.split('|');
          } else if (Array.isArray(filters.subcategory)) {
            newSubcategoryFilter = filters.subcategory;
          }
          if (typeof filters.segmento === 'string' && filters.segmento.length > 0) {
            newSegmentoFilter = filters.segmento.split('|');
          } else if (Array.isArray(filters.segmento)) {
            newSegmentoFilter = filters.segmento;
          }

          // Aplicar todos los cambios de estado de una vez
          // Primero establecer los filtros pendientes
          setMarcaFilter(newMarcaFilter);
          setSitioFilter(newSitioFilter);
          setCategoriaFilter(newCategoriaFilter);
          setSubcategoryFilter(newSubcategoryFilter);
          setSegmentoFilter(newSegmentoFilter);
          setPendingRemoveSubcategorias([]);
          setPendingRemoveSegmentos([]);
          setMarcaColumnName(newMarcaColumnName);
          setSitioColumnName(newSitioColumnName);
          setCategoriaColumnName(newCategoriaColumnName);
          setPriceFromFilter(filters.price_from || '');
          setPriceToFilter(filters.price_to || '');
          setPriceColumnName(filters.price_column || priceColumnName);
          setMaxPageFilter(filters.max_page || '');
          setMaxOrderFilter(filters.max_order || '');
          setDescuentoFilter(filters.descuento_filter || 'all');
          // PT-#95: saved filters viejos pueden traer `in_out_filter` (feature In/Out
          // eliminada); se ignora a propósito, no se restaura ni se reenvía. Tampoco se
          // guarda en el original, para que no figure como "cambio" contra el estado actual.
          const { in_out_filter: _ignoredInOut, ...originalFilters } = filters;

          // Establecer el filtro activo
          if (filterName) {
            setActiveFilterName(filterName);
            setActiveFilterOriginal(originalFilters);
          }
          
          // Aplicar todos los filtros de una vez
          setAppliedSearchTermOperators(filters.search_operators || {});
          setAppliedMarcaFilter(newMarcaFilter);
          setAppliedSitioFilter(newSitioFilter);
          // Pre-sync refs del cascade ANTES de setApplied*: un saved filter trae
          // cat+subcat+segment juntos intencionalmente. Sin esto, el cascade (~385)
          // borraría subcat/segment recién cargados (PT-#46 §5, filtros fantasma).
          prevCatRef.current = (newCategoriaFilter || []).join('|');
          prevSubRef.current = (newSubcategoryFilter || []).join('|');
          setAppliedCategoriaFilter(newCategoriaFilter);
          setAppliedSubcategoryFilter(newSubcategoryFilter);
          setAppliedSegmentoFilter(newSegmentoFilter);
          setAppliedPriceFromFilter(filters.price_from || '');
          setAppliedPriceToFilter(filters.price_to || '');
          setAppliedMaxPageFilter(filters.max_page || '');
          setAppliedMaxOrderFilter(filters.max_order || '');
          setAppliedDescuentoFilter(filters.descuento_filter || 'all');

          resolve(true);
        }, 0);
      });
      
      // Esperar un poco más para asegurar que todo se haya procesado
      await new Promise(resolve => setTimeout(resolve, 200));

      // PT-#58 — Si el filtro guardado trae los ejes de Tendencias, aplicarlos a la vista
      // (la card los aplica y regenera). No-op seguro si la card no está montada o el filtro
      // es legacy sin `trends` (retrocompatibilidad).
      if (filters.trends) {
        applyTrendsSnapshot(filters.trends);
      }

      // PT-#61 — Si el filtro guardado trae los ejes de Exportar, aplicarlos a esa vista.
      // No-op seguro si el panel no está montado o el filtro es legacy sin `export`.
      if (filters.export) {
        applyExportSnapshot(filters.export);
      }

    } finally {
      // Siempre desactivar el indicador de carga
      setIsApplyingSavedFilter(false);
    }
  };

  // PT-#46/PT-#61: scope del filtro guardado según la sección activa.
  // Tendencias y Exportar son multi-toma (multi); Estadísticas/Publicaciones usan una (single).
  const currentTomaScope: 'single' | 'multi' =
    (activeTab === 'tendencias' || activeTab === 'exportar') ? 'multi' : 'single';

  // PT-#46: los filtros son globales pero se discriminan por scope. Cada sección sólo
  // ve/aplica los que matchean su scope (legacy sin campo → 'single').
  const scopedSavedFilters = useMemo(
    () => savedFilters.filter((f) => normalizeTomaScope(f) === currentTomaScope),
    [savedFilters, currentTomaScope]
  );

  // Manejar guardado de filtro
  const handleSaveFilter = async (name: string, isPublic: boolean, description?: string) => {
    const currentFilters = getCurrentFiltersForSaving();
    await saveFilter(name, currentFilters, isPublic, description, undefined, currentTomaScope);
  };

  // Manejar actualización de filtro
  const handleUpdateFilter = async (filterId: string, name: string, isPublic: boolean) => {
    const currentFilters = getCurrentFiltersForSaving();
    await updateFilter(filterId, { name, filters: currentFilters, isPublic });
  };

  // Obtener filtro coincidente y diferencias
  // IMPORTANTE: Memoizar estas operaciones costosas para evitar recálculos en cada render
  const currentFiltersForComparison = useMemo(() => {
    return getCurrentFiltersForSaving();
  }, [
    globalFilter,
    appliedSearchTermOperators,
    appliedMarcaFilter,
    appliedSitioFilter,
    appliedCategoriaFilter,
    appliedPriceFromFilter,
    appliedPriceToFilter,
    appliedMaxPageFilter,
    appliedMaxOrderFilter,
    appliedDescuentoFilter
  ]);

  const matchingFilter = useMemo(() => {
    if (!savedFilters || savedFilters.length === 0 || loadingSavedFilters) return null;
    return findMatchingFilter(currentFiltersForComparison);
  }, [currentFiltersForComparison, savedFilters, loadingSavedFilters, findMatchingFilter]);

  const filterDifference = useMemo(() => {
    if (!matchingFilter) return null;
    try {
      if (hasChanges(currentFiltersForComparison, matchingFilter.filters)) {
        return compareFilters(currentFiltersForComparison, matchingFilter.filters);
      }
    } catch (error) {
      // Error silenciado
    }
    return null;
  }, [currentFiltersForComparison, matchingFilter, hasChanges, compareFilters]);

  useEffect(() => {
    if (isLoadingClients) return;

    if (!isInAdminMode && selectedClient?.clientId !== 'admin-mode') {
      checkConnection();
    } else if (isInAdminMode || selectedClient?.clientId === 'admin-mode') {
      setConnectionStatus('connected');
      setLoading(false);
    } else if (!selectedClient) {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingClients, selectedClient, isInAdminMode]);

  // Re-cargar tablas cuando cambie el idioma para actualizar displayNames
  useEffect(() => {
    // Forzar re-render cuando cambie el idioma usando update funcional
    setTables(prevTables => {
      // Solo actualizar si hay tablas previas
      if (prevTables.length > 0) {
        return [...prevTables];
      }
      return prevTables;
    });
  }, [i18n.language]);
  
  // Monitor tables changes and update tablesLoaded
  useEffect(() => {
    if (tables.length > 0 && !tablesLoaded) {
      setTablesLoaded(true);
    }
  }, [tables, tablesLoaded]);
  

  // Estado para controlar la transición entre tablas
  const [isTableTransitioning, setIsTableTransitioning] = useState(false);

  // Limpiar filtros cuando cambie la tabla seleccionada (skip for admin mode)
  useEffect(() => {
    
    if (!selectedTable) return;
    
    // Skip if in admin mode
    if (isInAdminMode || selectedClient?.clientId === 'admin-mode') {
      return;
    }
    
    // Marcar que estamos en transición
    setIsTableTransitioning(true);
    
    setSharedFilters({});
    setGlobalFilter('');
    setMarcaFilter([]);
    setSitioFilter([]);
    setCategoriaFilter([]);
    setSubcategoryFilter([]);
    setAppliedMarcaFilter([]);
    setAppliedSitioFilter([]);
    setAppliedCategoriaFilter([]);
    setAppliedSubcategoryFilter([]);
    setPriceFromFilter('');
    setPriceToFilter('');
    setAppliedPriceFromFilter('');
    setAppliedPriceToFilter('');
    setMaxPageFilter('');
    setMaxOrderFilter('');
    setAppliedMaxPageFilter('');
    setAppliedMaxOrderFilter('');
    setDescuentoFilter('all');
    setAppliedDescuentoFilter('all');
    setActiveFilterName(null);
    setActiveFilterOriginal(null);
    setSearchTermOperators({});
    setAppliedSearchTermOperators({});
  }, [selectedTable, isInAdminMode, selectedClient]);

  // Cargar esquema para obtener nombres de columnas
  // BUG-012 FIX (2026-06-05): aceptar AbortSignal para cancelar fetches stale
  // cuando el usuario cambia de cliente/tabla rápidamente. Sin esto, el response
  // del cliente anterior llegaba después del re-render y disparaba un 404
  // (porque la tabla pertenece a otro cliente/DB).
  const loadTableSchema = async (tableName: string, signal?: AbortSignal) => {
    try {
      const response = await api.get(`/api/tables/${tableName}/schema`, { signal });
      // El endpoint retorna { schema, tagColumns, hasTags, columnConfig }
      const schema = Array.isArray(response.data) ? response.data : response.data?.schema;
      // Stash columnConfig (puede ser null si el cliente no tiene doc).
      // Usado para opt-in features (ej. filtro de subcategoría per cliente).
      const cc = (response.data && typeof response.data === 'object' && (response.data as any).columnConfig) || null;
      setTableColumnConfig(cc);

      if (!Array.isArray(schema)) {
        console.warn('Dashboard - Schema is not an array:', typeof response.data);
        return;
      }

      // Encontrar nombres de columnas (case-insensitive)
      const marcaCol = schema.find((col: any) => {
        const fieldLower = col.Field.toLowerCase();
        return fieldLower === 'marca' || fieldLower === 'brand' || fieldLower.includes('marca');
      });
      
      // Para sitio, usar directamente 'sitio' en minúsculas
      const sitioColExact = { Field: 'sitio' };
      
      // Verificar que no estamos confundiendo Sitio con Seller
      
      const categoriaCol = schema.find((col: any) => {
        const fieldLower = col.Field.toLowerCase();
        return fieldLower === 'categoria' || 
               fieldLower === 'category' || 
               fieldLower === 'cat' ||
               fieldLower.includes('categ');
      });
      
      // Si no encontramos 'categoria', buscar la columna exacta 'Cat' con mayúscula
      const categoriaColExact = categoriaCol || schema.find((col: any) => col.Field === 'Cat');
      
      const priceCol = schema.find((col: any) => {
        const fieldLower = col.Field.toLowerCase();
        return fieldLower === 'sale_price' || 
               fieldLower === 'precio_venta' || 
               fieldLower === 'precio' || 
               fieldLower === 'original_price' || 
               fieldLower === 'price' || 
               fieldLower.includes('precio');
      });
      
      const detectedColumns = {
        marca: marcaCol?.Field,
        sitio: sitioColExact?.Field,
        categoria: categoriaColExact?.Field,
        precio: priceCol?.Field
      };
      
      
      // Usar tags si existen para esta toma, sino columnas por defecto.
      // EXCEPCIÓN F2-BUG-4 (TIA + mergeWithCatalog): si el cliente tiene
      // columnConfig.core.{marca|categoria}.mergeWithCatalog seteado, catalog
      // GANA sobre tags — porque tags puede tener apenas N rows (de syncs
      // catalogToTagsSync parciales) mientras catalog tiene la fuente real.
      // El backend en /distinct/:column detecta mergeWithCatalog y hace el JOIN.
      const hasTags = response.data?.hasTags;
      const coreMarcaMerge = !!(cc?.core?.marca?.enabled !== false && cc?.core?.marca?.mergeWithCatalog);
      const coreCategoriaMerge = !!(cc?.core?.categoria?.enabled !== false && cc?.core?.categoria?.mergeWithCatalog);
      const useTagMarca = !coreMarcaMerge && hasTags?.MARCA === true;
      const useTagCategoria = !coreCategoriaMerge && hasTags?.CATEGORIA === true;

      // Guardar SIEMPRE la columna toma real de marca (aunque usemos tags para
      // el display), para que el filtro de /data pueda leer la misma fuente que
      // el dropdown (facets → toma.Marca). Ver FIX Adidas 2026-07-22.
      setMarcaTomaColumn(marcaCol?.Field || 'Marca');
      if (useTagMarca) {
        setMarcaColumnName('__tag:MARCA');
      } else {
        if (marcaCol) setMarcaColumnName(marcaCol.Field);
      }
      if (useTagCategoria) {
        setCategoriaColumnName('__tag:CATEGORIA');
      } else {
        if (categoriaColExact) setCategoriaColumnName(categoriaColExact.Field);
      }
      if (sitioColExact) {
        setSitioColumnName(sitioColExact.Field);
      }
      if (priceCol) setPriceColumnName(priceCol.Field);

      // Load filter options with the detected column names
      const marcaForOptions = useTagMarca ? '__tag:MARCA' : detectedColumns.marca;
      const categoriaForOptions = useTagCategoria
        ? '__tag:CATEGORIA'
        : detectedColumns.categoria;
      await loadFilterOptions(tableName, marcaForOptions, detectedColumns.sitio, categoriaForOptions, signal);

      return detectedColumns;

    } catch (err: any) {
      // BUG-012 FIX (2026-06-05): silenciar errores de cancelación.
      // axios marca cancelaciones como ERR_CANCELED o err.name === 'CanceledError'.
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || signal?.aborted) {
        return { marca: undefined, sitio: undefined, categoria: undefined, precio: undefined };
      }
      console.error('❌ Dashboard - Error loading schema:', err);
      return { marca: undefined, sitio: undefined, categoria: undefined, precio: undefined };
    }
  };

  // Cargar opciones para los filtros dropdown
  // BUG-012 FIX (2026-06-05): aceptar signal para cancelación encadenada desde
  // loadTableSchema cuando el usuario cambia de cliente/tabla.
  const loadFilterOptions = useCallback(async (tableName: string, marcaCol?: string, sitioCol?: string, categoriaCol?: string, signal?: AbortSignal) => {
    if (!tableName) return;

    const useMarcaCol = marcaCol || marcaColumnName;
    const useSitioCol = sitioCol || sitioColumnName;
    const useCategoriaCol = categoriaCol || categoriaColumnName;

    // Cada llamada en su propio try-catch para que un error no afecte a las demás
    // Helper: parsea response que puede venir como shape vieja (string[]) o
    // shape nueva ({ values: [{value, n}], total, hasEmpty }).
    // Devuelve { options: string[], meta: OptionsMeta }.
    const parseDistinctResponse = (data: any): { options: string[]; meta: OptionsMeta } => {
      if (Array.isArray(data)) {
        // shape vieja: string[]
        const options = data.filter((item: any) => item && typeof item === 'string' && item.trim() !== '');
        return { options, meta: null };
      }
      if (data && Array.isArray(data.values)) {
        const counts: Record<string, number> = {};
        const options: string[] = [];
        // Ordenar: '(vacío)' al final, resto alfabético.
        const sorted = [...data.values].sort((a: any, b: any) => {
          if (a.value === '(vacío)') return 1;
          if (b.value === '(vacío)') return -1;
          return String(a.value).localeCompare(String(b.value));
        });
        for (const v of sorted) {
          if (v && typeof v.value === 'string') {
            options.push(v.value);
            counts[v.value] = Number(v.n) || 0;
          }
        }
        return { options, meta: { counts, total: Number(data.total) || options.length } };
      }
      return { options: [], meta: null };
    };

    // BUG-012 FIX (2026-06-05): helper para detectar cancel y silenciar warn.
    const isCanceled = (err: any) => err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || signal?.aborted;

    if (useMarcaCol) {
      try {
        let marcaResponse;
        if (useMarcaCol.startsWith('__tag:')) {
          const tagKey = useMarcaCol.replace('__tag:', '');
          marcaResponse = await api.get(`/api/tables/${tableName}/distinct-tag/${tagKey}?withCounts=1`, { signal });
        } else {
          marcaResponse = await api.get(`/api/tables/${tableName}/distinct/${useMarcaCol}?withCounts=1`, { signal });
        }
        const { options, meta } = parseDistinctResponse(marcaResponse.data);
        setMarcaOptions(options);
        setMarcaOptionsMeta(meta);
      } catch (err: any) {
        if (isCanceled(err)) return;
        console.warn(`No se pudieron cargar marcas (columna: ${useMarcaCol})`);
        setMarcaOptions([]);
        setMarcaOptionsMeta(null);
      }
    }

    if (useSitioCol) {
      try {
        const sitioResponse = await api.get(`/api/tables/${tableName}/distinct/sitio?withCounts=1`, { signal });
        const { options, meta } = parseDistinctResponse(sitioResponse.data);
        setSitioOptions(options);
        setSitioOptionsMeta(meta);
      } catch (err: any) {
        if (isCanceled(err)) return;
        console.warn('No se pudieron cargar sitios');
        setSitioOptions([]);
        setSitioOptionsMeta(null);
      }
    }

    if (useCategoriaCol) {
      try {
        let categoriaResponse;
        if (useCategoriaCol.startsWith('__tag:')) {
          // Cargar categorías desde tags
          const tagKey = useCategoriaCol.replace('__tag:', '');
          categoriaResponse = await api.get(`/api/tables/${tableName}/distinct-tag/${tagKey}?withCounts=1`, { signal });
        } else {
          categoriaResponse = await api.get(`/api/tables/${tableName}/distinct/${useCategoriaCol}?withCounts=1`, { signal });
        }
        const { options, meta } = parseDistinctResponse(categoriaResponse.data);
        setCategoriaOptions(options);
        setCategoriaOptionsMeta(meta);
      } catch (err: any) {
        if (isCanceled(err)) return;
        console.warn(`No se pudieron cargar categorías (columna: ${useCategoriaCol})`);
        setCategoriaOptions([]);
        setCategoriaOptionsMeta(null);
      }
    }
  }, [marcaColumnName, sitioColumnName, categoriaColumnName]);

  // Cargar esquema y opciones cuando cambia la tabla (skip for admin mode)
  // BUG-012 FIX (2026-06-05): usar AbortController para cancelar fetches stale
  // cuando el usuario cambia rápido de cliente (Samsung→Adidas→TIA). Sin esto,
  // requests in-flight con tableName viejo retornaban 404 (tabla pertenece a
  // otro cliente/DB) y dispara console.error visible al user.
  useEffect(() => {
    if (isInAdminMode || selectedClient?.clientId === 'admin-mode') {
      return;
    }

    const controller = new AbortController();

    const loadData = async () => {
      if (selectedTable && isTableTransitioning) {
        try {
          await loadTableSchema(selectedTable, controller.signal);
        } finally {
          if (!controller.signal.aborted) {
            setIsTableTransitioning(false);
          }
        }
      }
    };

    loadData();

    return () => {
      controller.abort();
    };
  }, [selectedTable, isTableTransitioning, isInAdminMode, selectedClient]);

  // Monitor globalFilter changes for debugging
  useEffect(() => {
  }, [globalFilter, searchTermOperators, appliedSearchTermOperators]);

  // Actualizar filtros compartidos cuando cambian los filtros individuales (skip for admin mode)
  useEffect(() => {
    
    // TEMPORALMENTE DESHABILITADO PARA DEBUGGING
    return;
    
    // Skip if in admin mode
    if (isInAdminMode || selectedClient?.clientId === 'admin-mode') {
      return;
    }
    
    // Usar un timeout para debounce y evitar múltiples actualizaciones rápidas
    const timeoutId = setTimeout(() => {
      
      const filters: SharedFilters = {};
      
      if (globalFilter) {
        filters['global_search'] = globalFilter;
      }
      
      // Agregar operadores de búsqueda APLICADOS si existen
      if (Object.keys(appliedSearchTermOperators).length > 0) {
        filters['search_operators'] = appliedSearchTermOperators;
        // Si no hay globalFilter pero sí operadores, crear uno vacío para activar la búsqueda
        if (!globalFilter) {
          filters['global_search'] = '';
        }
      }
      
      if (appliedMarcaFilter.length > 0) {
        // Filtro de marca: usar la MISMA fuente que el dropdown (facets/toma column).
        // Ver brandFilterColumn (FIX Adidas 2026-07-22).
        const columnName = brandFilterColumn;
        filters[columnName] = appliedMarcaFilter.join('|');
      } else {
      }
      if (appliedSitioFilter.length > 0) {
        // Usar el nombre de columna correcto o fallback basado en esquema
        // IMPORTANTE: Usar 'sitio' en minúscula como clave del filtro
        const columnName = 'sitio'; // Siempre usar 'sitio' como clave
        filters[columnName] = appliedSitioFilter.join('|');
      } else {
      }
      if (appliedCategoriaFilter.length > 0) {
        // Usar el nombre de columna correcto o 'Cat' como fallback
        const columnName = categoriaColumnName || 'Cat';
        filters[columnName] = appliedCategoriaFilter.join('|');
      } else {
      }
      
      if (appliedPriceFromFilter) {
        filters['price_from'] = appliedPriceFromFilter;
      }
      if (appliedPriceToFilter) {
        filters['price_to'] = appliedPriceToFilter;
      }
      if (appliedPriceFromFilter || appliedPriceToFilter) {
        // SIEMPRE usar sale_price para filtros de precio (precio final que paga el usuario)
        filters['price_column'] = 'sale_price';
      }
      
      if (appliedMaxPageFilter) {
        filters['max_page'] = appliedMaxPageFilter;
      }
      if (appliedMaxOrderFilter) {
        filters['max_order'] = appliedMaxOrderFilter;
      }
      
      // Agregar filtro de descuentos (siempre para que las estadísticas se actualicen)
      filters['descuento_filter'] = appliedDescuentoFilter;

      setSharedFilters(filters);
    }, 100); // 100ms de debounce

    return () => clearTimeout(timeoutId);
  }, [globalFilter, appliedMarcaFilter, appliedSitioFilter, appliedCategoriaFilter, appliedPriceFromFilter, appliedPriceToFilter, appliedMaxPageFilter, appliedMaxOrderFilter, appliedDescuentoFilter, activeTab, marcaColumnName, brandFilterColumn, sitioColumnName, categoriaColumnName, priceColumnName, appliedSearchTermOperators, isInAdminMode, selectedClient]);

  // Todos los filtros se aplican únicamente con el botón FILTRAR

  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-dropdown') && showUserDropdown) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Cargar todos los productos SOLO cuando entran a la pestaña Comparar (skip for admin mode).
  // Optimización (mayo 2026): antes este efecto disparaba un GET /api/tables/<x>/data?limit=100000
  // (~40s, 100k filas) en cada cambio de tabla, aunque el usuario estuviera en Publicaciones.
  // El consumidor real (`Comparar` -> `EditGroupProductsModal`) solo necesita el dataset cuando
  // se abre la modal "agregar productos a un grupo", así que postergamos la carga al activeTab
  // 'comparar'. Si el usuario nunca entra a Comparar, no se paga ese costo.
  useEffect(() => {
    const shouldLoad =
      selectedTable &&
      !isInAdminMode &&
      selectedClient?.clientId !== 'admin-mode' &&
      activeTab === 'comparar';

    if (shouldLoad) {
      loadAllProducts(selectedTable);
    } else {
      setAllProducts([]);
    }

    return () => {
      if (loadAllProductsControllerRef.current) {
        loadAllProductsControllerRef.current.abort();
      }
    };
  }, [selectedTable, isInAdminMode, selectedClient, activeTab]);

  // Retrasar la muestra de errores para dar tiempo al video de bienvenida
  useEffect(() => {
    if (error) {
      // Esperar 3 segundos antes de mostrar el error
      const timer = setTimeout(() => {
        setShowError(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  const checkConnection = async () => {
    try {
      const response = await api.get('/api/health');
      if (response.data.status === 'OK') {
        setConnectionStatus('connected');
        setError(null);
        await loadTables();
      }
    } catch (err: any) {
      console.error('Connection check failed:', err);
      setConnectionStatus('error');
      setError('No se pudo conectar al servidor');
      // Try to load tables anyway in case only health check is failing
      await loadTables();
    }
  };

  const loadTables = async () => {
    try {
      setLoading(true);
      
      // Get client headers
      const headers: any = {};
      const selectedClientStr = getStoredSelectedClientRaw();
      if (selectedClientStr) {
        try {
          const client = JSON.parse(selectedClientStr);
          if (client.clientId && client.clientId !== 'admin-mode') {
            headers['X-Client-Id'] = client.clientId;
            headers['X-Client-Name'] = client.clientName;
          }
        } catch (e) {
          console.error('Error parsing client:', e);
        }
      }
      
      // Agregar timestamp para evitar caché
      const timestamp = new Date().getTime();
      const response = await api.get(`/api/tomas-con-fechas?_t=${timestamp}`, { headers });
      
      // Log específico para debugging de Adidas
      if (selectedClientStr) {
        try {
          const client = JSON.parse(selectedClientStr);
          if (client.clientName?.toLowerCase().includes('adidas')) {
          }
        } catch (e) {
          console.error('Error parsing client for debugging:', e);
        }
      }
      
      setTables(response.data);
      setTablesLoaded(true);
      
      // Seleccionar la primera tabla por defecto si existe
      if (response.data.length > 0) {
        setSelectedTable(response.data[0].tableName);
      } else {
      }
    } catch (err: any) {
      console.error('Error loading tables:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        headers: err.config?.headers,
        url: err.config?.url
      });
      
      // Check if it's a client configuration issue
      if (err.response?.status === 404 && err.response?.data?.error === 'Client not found') {
        setError(t('errors.client_not_found') || 'Cliente no encontrado. Por favor, seleccione un cliente válido.');
      } else if (err.response?.status === 403) {
        setError(t('errors.client_not_active') || 'El cliente no está activo.');
      } else {
        setError(t('errors.loading_data_sources'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear fecha completa (para títulos de tabla con prefijo)
  const formatDate = (date: Date): string => {
    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    
    const day = date.getDate();
    const monthKey = monthKeys[date.getMonth()];
    const month = t(`months.${monthKey}`);
    const year = date.getFullYear();
    const preposition = t('date.date_preposition');
    const dataFrom = t('date.data_from');
    
    return `${dataFrom} ${day} ${preposition} ${month}, ${year}`;
  };

  // Función para formatear solo la fecha (para dropdown sin prefijo)
  const formatDateOnly = (date: Date): string => {
    const monthKeys = [
      'january', 'february', 'march', 'april', 'may', 'june',
      'july', 'august', 'september', 'october', 'november', 'december'
    ];
    
    const day = date.getDate();
    const monthKey = monthKeys[date.getMonth()];
    const month = t(`months.${monthKey}`);
    const year = date.getFullYear();
    const preposition = t('date.date_preposition');
    
    return `${day} ${preposition} ${month}, ${year}`;
  };

  // Función para obtener el nombre completo para mostrar de una tabla (con prefijo)
  const getTableDisplayName = (tableName: string): string => {
    const toma = tables.find(t => t.tableName === tableName);
    
    if (toma && toma.fecha) {
      // Si tiene fecha, formatearla usando las traducciones
      let date: Date;
      
      // Manejar diferentes formatos de fecha
      if (typeof toma.fecha === 'string') {
        // Extraer año, mes y día directamente del string
        // Soporta tanto YYYY-MM-DD como YYYY-MM-DDTHH:mm:ss.sssZ
        const dateMatch = toma.fecha.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          const [, year, month, day] = dateMatch;
          // Crear fecha local sin conversión de timezone
          // Usar setHours para garantizar que sea medianoche hora local
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          date.setHours(0, 0, 0, 0);
        } else {
          // Si no coincide con el formato esperado, intentar parsear
          date = new Date(toma.fecha);
        }
      } else {
        // Si no es string, intentar convertir
        date = new Date(toma.fecha);
      }
      
      // Verificar que la fecha sea válida
      if (!isNaN(date.getTime())) {
        return formatDate(date);
      }
    }
    
    // Si no tiene fecha, extraer el número de toma para mostrar algo legible
    const tomaMatch = tableName.match(/^toma_(\d+)/);
    if (tomaMatch) {
      return `Toma #${tomaMatch[1]}`;
    }

    return tableName;
  };

  // Función para obtener el label del dropdown: "D de Mes, YYYY (Toma NN)"
  const getTableDropdownName = (tableName: string): string => {
    const toma = tables.find(t => t.tableName === tableName);

    // Extraer número de toma del tableName (soporta toma_NN_..., toma-NN-..., tomaNN)
    const tomaMatch = tableName.match(/toma[_-]?(\d+)/i);
    const tomaNumber = tomaMatch ? tomaMatch[1] : null;

    // Meses en español capitalizados (indexados por getMonth() 0-11)
    const MESES_ES = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    if (toma && toma.fecha) {
      // Si tiene fecha, formatearla
      let date: Date;

      // Manejar diferentes formatos de fecha
      if (typeof toma.fecha === 'string') {
        // Extraer año, mes y día directamente del string
        // Soporta tanto YYYY-MM-DD como YYYY-MM-DDTHH:mm:ss.sssZ
        const dateMatch = toma.fecha.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dateMatch) {
          const [, year, month, day] = dateMatch;
          // Crear fecha local sin conversión de timezone
          // Usar setHours para garantizar que sea medianoche hora local
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          date.setHours(0, 0, 0, 0);
        } else {
          // Si no coincide con el formato esperado, intentar parsear
          date = new Date(toma.fecha);
        }
      } else {
        // Si no es string, intentar convertir
        date = new Date(toma.fecha);
      }

      // Verificar que la fecha sea válida
      if (!isNaN(date.getTime())) {
        const d = date.getDate();
        const mesNombre = MESES_ES[date.getMonth()];
        const y = date.getFullYear();
        const fechaFmt = `${d} de ${mesNombre}, ${y}`;
        return tomaNumber ? `${fechaFmt} (Toma ${tomaNumber})` : fechaFmt;
      }
    }

    // Si no tiene fecha, extraer el número de toma para mostrar algo legible
    if (tomaNumber) {
      return `Toma #${tomaNumber}`;
    }

    return tableName;
  };

  // Función para obtener información de la toma actual para tooltips
  const getCurrentTableInfo = (tableName: string): { name: string; date?: string } => {
    const toma = tables.find(t => t.tableName === tableName);
    
    if (toma && toma.fecha) {
      return {
        name: getTableDisplayName(tableName),
        date: toma.fecha // Pasar la fecha original sin formatear
      };
    }
    
    // Si no tiene fecha en la base de datos, devolver solo el nombre
    
    return {
      name: tableName
    };
  };

  // Función para cambiar operador de un término
  const toggleSearchTermOperator = (term: string) => {
    // Contar cuántos términos hay
    const terms = globalFilter.trim().split(/\s+/).filter(t => t.length > 0);
    const termCount = terms.length;
    
    // Usar el operador aplicado actual como base
    const currentOperator = appliedSearchTermOperators[term] || 'OR';
    
    let nextOperator: 'OR' | 'AND' | 'NOT';
    
    if (termCount === 1) {
      // Si hay solo un término, solo puede ser AND o NOT
      nextOperator = currentOperator === 'AND' ? 'NOT' : 'AND';
    } else {
      // Comportamiento normal si hay múltiples términos
      nextOperator = currentOperator === 'OR' ? 'AND' : currentOperator === 'AND' ? 'NOT' : 'OR';
    }
    
    setSearchTermOperators(prev => {
      return { ...prev, [term]: nextOperator };
    });
  };

  // Función para remover un término específico
  const removeSearchTerm = (termToRemove: string) => {
    const terms = globalFilter.trim().split(/\s+/).filter(t => t.length > 0);
    const newTerms = terms.filter(term => term !== termToRemove);
    setGlobalFilter(newTerms.join(' '));
    
    // Si queda solo un término después de eliminar, cambiarlo a AND
    if (newTerms.length === 1) {
      const remainingTerm = newTerms[0];
      setSearchTermOperators(prev => {
        const newOperators = { ...prev };
        // Eliminar el operador del término eliminado
        delete newOperators[termToRemove];
        // Si el término restante no es NOT, cambiarlo a AND
        if (newOperators[remainingTerm] !== 'NOT') {
          newOperators[remainingTerm] = 'AND';
        }
        return newOperators;
      });
      
      // También actualizar los operadores aplicados
      setAppliedSearchTermOperators(prev => {
        const newOperators = { ...prev };
        delete newOperators[termToRemove];
        if (newOperators[remainingTerm] !== 'NOT') {
          newOperators[remainingTerm] = 'AND';
        }
        return newOperators;
      });
    } else {
      // Solo eliminar el operador del término eliminado
      setSearchTermOperators(prev => {
        const newOperators = { ...prev };
        delete newOperators[termToRemove];
        return newOperators;
      });
      
      setAppliedSearchTermOperators(prev => {
        const newOperators = { ...prev };
        delete newOperators[termToRemove];
        return newOperators;
      });
    }
  };

  // Función para verificar si hay filtros pendientes
  const hasPendingFilters = () => {
    return (
      marcaFilter.length > 0 ||
      sitioFilter.length > 0 ||
      categoriaFilter.length > 0 ||
      subcategoryFilter.length > 0 ||
      segmentoFilter.length > 0 ||
      priceFromFilter !== appliedPriceFromFilter ||
      priceToFilter !== appliedPriceToFilter ||
      maxPageFilter !== appliedMaxPageFilter ||
      maxOrderFilter !== appliedMaxOrderFilter ||
      descuentoFilter !== appliedDescuentoFilter ||
      JSON.stringify(searchTermOperators) !== JSON.stringify(appliedSearchTermOperators)
    );
  };

  // Función para aplicar todos los filtros pendientes
  const applyAllPendingFilters = () => {
    
    // No aplicar filtros de búsqueda aquí si ya se aplicaron en handleSearchFilter
    // Solo aplicar si hay operadores pendientes diferentes a los aplicados
    if (JSON.stringify(searchTermOperators) !== JSON.stringify(appliedSearchTermOperators)) {
      setAppliedSearchTermOperators(searchTermOperators);
    }
    
    // Aplicar filtros de marcas, sitios y categorías - combinar sin duplicados + restar pendingRemoves
    // pendingRemove* contiene items que el user marcó para quitar via X de pill applied.
    if (marcaFilter.length > 0 || appliedMarcaFilter.length > 0 || pendingRemoveMarcas.length > 0) {
      const combined = [...appliedMarcaFilter, ...marcaFilter];
      const uniqueMarcas = combined.filter((item, index) => combined.indexOf(item) === index);
      const finalMarcas = uniqueMarcas.filter(m => !pendingRemoveMarcas.includes(m));
      setAppliedMarcaFilter(finalMarcas);
      setMarcaFilter([]); // Limpiar pending additions
      setPendingRemoveMarcas([]); // Limpiar pending removals
    }

    if (sitioFilter.length > 0 || pendingRemoveSitios.length > 0) {
      const combined = [...appliedSitioFilter, ...sitioFilter];
      const uniqueSitios = combined.filter((item, index) => combined.indexOf(item) === index);
      const finalSitios = uniqueSitios.filter(s => !pendingRemoveSitios.includes(s));
      setAppliedSitioFilter(finalSitios);
      setSitioFilter([]);
      setPendingRemoveSitios([]);
    }

    // Guardar las categorías a aplicar para usarlas en el setTimeout
    const categoriasToApply = (() => {
      if (categoriaFilter.length > 0 || appliedCategoriaFilter.length > 0 || pendingRemoveCategorias.length > 0) {
        const combined = [...appliedCategoriaFilter, ...categoriaFilter];
        const uniqueCategorias = combined.filter((item, index) => combined.indexOf(item) === index);
        const finalCategorias = uniqueCategorias.filter(c => !pendingRemoveCategorias.includes(c));
        // Pre-sync la ref del cascade: este batch ya computó los hijos finales
        // (subcat/segment) intencionalmente. Sin esto, el effect de cascade (~385)
        // borraría subcat/segment co-aplicados en el mismo Filtrar (BUG2 del QA report).
        prevCatRef.current = finalCategorias.join('|');
        setAppliedCategoriaFilter(finalCategorias);
        setCategoriaFilter([]);
        setPendingRemoveCategorias([]);
        return finalCategorias;
      } else {
        return appliedCategoriaFilter;
      }
    })();

    // Subcategorías a aplicar (opt-in per cliente)
    const subcategoriasToApply = (() => {
      if (subcategoryFilter.length > 0 || appliedSubcategoryFilter.length > 0 || pendingRemoveSubcategorias.length > 0) {
        const combined = [...appliedSubcategoryFilter, ...subcategoryFilter];
        const uniqueSubs = combined.filter((item, index) => combined.indexOf(item) === index);
        const finalSubs = uniqueSubs.filter(s => !pendingRemoveSubcategorias.includes(s));
        // Pre-sync la ref del cascade Subcat→Seg: preserva el segment co-aplicado.
        prevSubRef.current = finalSubs.join('|');
        setAppliedSubcategoryFilter(finalSubs);
        setSubcategoryFilter([]);
        setPendingRemoveSubcategorias([]);
        return finalSubs;
      } else {
        return appliedSubcategoryFilter;
      }
    })();

    // Segmentos a aplicar (opt-in per cliente)
    const segmentosToApply = (() => {
      if (segmentoFilter.length > 0 || appliedSegmentoFilter.length > 0 || pendingRemoveSegmentos.length > 0) {
        const combined = [...appliedSegmentoFilter, ...segmentoFilter];
        const uniqueSegs = combined.filter((item, index) => combined.indexOf(item) === index);
        const finalSegs = uniqueSegs.filter(s => !pendingRemoveSegmentos.includes(s));
        setAppliedSegmentoFilter(finalSegs);
        setSegmentoFilter([]);
        setPendingRemoveSegmentos([]);
        return finalSegs;
      } else {
        return appliedSegmentoFilter;
      }
    })();

    // Aplicar otros filtros
    setAppliedPriceFromFilter(priceFromFilter);
    setAppliedPriceToFilter(priceToFilter);
    setAppliedMaxPageFilter(maxPageFilter);
    setAppliedMaxOrderFilter(maxOrderFilter);
    setAppliedDescuentoFilter(descuentoFilter);

    // Forzar actualización inmediata de sharedFilters con TODOS los filtros aplicados
    // Usar los valores guardados para evitar problemas de closure. Restar pendingRemoves.
    const marcasToApply = [...appliedMarcaFilter, ...marcaFilter]
      .filter((item, index, arr) => arr.indexOf(item) === index)
      .filter(m => !pendingRemoveMarcas.includes(m));
    const sitiosToApply = [...appliedSitioFilter, ...sitioFilter]
      .filter((item, index, arr) => arr.indexOf(item) === index)
      .filter(s => !pendingRemoveSitios.includes(s));

    setTimeout(() => {
      const filters: SharedFilters = {};
      
      if (globalFilter) {
        filters['global_search'] = globalFilter;
      }
      
      if (Object.keys(appliedSearchTermOperators).length > 0) {
        filters['search_operators'] = appliedSearchTermOperators;
        if (!globalFilter) {
          filters['global_search'] = '';
        }
      }
      
      // Usar los valores pre-calculados
      if (marcasToApply.length > 0) {
        const columnName = brandFilterColumn;
        filters[columnName] = marcasToApply.join('|');
      }
      
      if (sitiosToApply.length > 0) {
        const columnName = 'sitio';
        filters[columnName] = sitiosToApply.join('|');
      }
      
      // Usar las categorías previamente calculadas
      
      if (categoriasToApply && categoriasToApply.length > 0) {
        const columnName = categoriaColumnName || 'Cat';
        filters[columnName] = categoriasToApply.join('|');
      } else {
      }

      // Subcategory filter (catalog-side; backend resuelve vía c.subcategory IN (...))
      if (subcategoriasToApply && subcategoriasToApply.length > 0) {
        filters['subcategory'] = subcategoriasToApply.join('|');
      }

      // Segmento filter (catalog-side; backend resuelve vía c.segmento IN (...))
      if (segmentosToApply && segmentosToApply.length > 0) {
        filters['segmento'] = segmentosToApply.join('|');
      }

      // Usar los valores de filtros de precio que acabamos de aplicar
      if (priceFromFilter) {
        filters['price_from'] = priceFromFilter;
      }
      if (priceToFilter) {
        filters['price_to'] = priceToFilter;
      }
      if (priceFromFilter || priceToFilter) {
        filters['price_column'] = 'sale_price';
      }
      
      if (maxPageFilter) {
        filters['max_page'] = maxPageFilter;
      }
      if (maxOrderFilter) {
        filters['max_order'] = maxOrderFilter;
      }
      
      filters['descuento_filter'] = descuentoFilter;

      setSharedFilters(filters);
    }, 0);
  };

  // Manejador para el nuevo componente de búsqueda
  const handleSearchFilter = (terms: string[], operators: {[key: string]: 'OR' | 'AND' | 'NOT'}) => {

    const finishApply = debugStep('applyFilters', {
      terms: terms.length,
      marca: marcaFilter.length,
      sitio: sitioFilter.length,
      categoria: categoriaFilter.length,
    });

    // Validar precios antes de aplicar
    const priceErrors = validatePrices(priceFromFilter, priceToFilter);

    if (priceErrors.length > 0) {
      // Mostrar errores y no aplicar filtros
      setPriceValidationErrors(priceErrors);
      console.warn('❌ Errores de validación de precios:', priceErrors);
      finishApply({ aborted: 'priceValidation' });
      return;
    }
    
    // Limpiar errores si todo está bien
    setPriceValidationErrors([]);
    
    if (terms.length > 0) {
      // Separar términos NOT de los demás para no incluirlos en globalFilter
      const nonNotTerms = terms.filter(term => operators[term] !== 'NOT');
      const newGlobalFilter = nonNotTerms.join(' ');
      setGlobalFilter(newGlobalFilter);
      
      // Si hay un solo término sin operador definido, forzar AND
      const finalOperators = { ...operators };
      if (terms.length === 1 && !operators[terms[0]]) {
        finalOperators[terms[0]] = 'AND';
      }
      
      setSearchTermOperators(finalOperators);
      
      // Aplicar operadores inmediatamente también
      setAppliedSearchTermOperators(finalOperators);
    } else {
      setGlobalFilter('');
      setSearchTermOperators({});
      setAppliedSearchTermOperators({});
    }
    
    // Aplicar otros filtros pendientes pero sin sobrescribir los search terms
    
    // Aplicar filtros de marca, sitio, categoría sin tocar search terms
    // FIX 2026-05-30: aplicar pendingRemoves (X de pill applied) además de pending additions.
    if (marcaFilter.length > 0 || pendingRemoveMarcas.length > 0) {
      const combined = [...appliedMarcaFilter, ...marcaFilter];
      const uniqueMarcas = combined.filter((item, index) => combined.indexOf(item) === index);
      const finalMarcas = uniqueMarcas.filter(m => !pendingRemoveMarcas.includes(m));
      setAppliedMarcaFilter(finalMarcas);
      setMarcaFilter([]);
      setPendingRemoveMarcas([]);
    }

    if (sitioFilter.length > 0 || pendingRemoveSitios.length > 0) {
      const combined = [...appliedSitioFilter, ...sitioFilter];
      const uniqueSitios = combined.filter((item, index) => combined.indexOf(item) === index);
      const finalSitios = uniqueSitios.filter(s => !pendingRemoveSitios.includes(s));
      setAppliedSitioFilter(finalSitios);
      setSitioFilter([]);
      setPendingRemoveSitios([]);
    }

    if (categoriaFilter.length > 0 || pendingRemoveCategorias.length > 0) {
      const combined = [...appliedCategoriaFilter, ...categoriaFilter];
      const uniqueCategorias = combined.filter((item, index) => combined.indexOf(item) === index);
      const finalCategorias = uniqueCategorias.filter(c => !pendingRemoveCategorias.includes(c));
      // Pre-sync cascade ref: este Filtrar ya computó los hijos finales (subcat/segment)
      // intencionalmente. Sin esto el effect de cascade (~385) borra los hijos co-aplicados
      // en el mismo Filtrar (BUG2 / cascade #9 del QA report). Espeja applyAllPendingFilters.
      prevCatRef.current = finalCategorias.join('|');
      setAppliedCategoriaFilter(finalCategorias);
      setCategoriaFilter([]);
      setPendingRemoveCategorias([]);
    }

    if (subcategoryFilter.length > 0 || pendingRemoveSubcategorias.length > 0) {
      const combined = [...appliedSubcategoryFilter, ...subcategoryFilter];
      const uniqueSubs = combined.filter((item, index) => combined.indexOf(item) === index);
      const finalSubs = uniqueSubs.filter(s => !pendingRemoveSubcategorias.includes(s));
      // Pre-sync cascade Subcat→Seg: preserva el segment co-aplicado en el mismo Filtrar.
      prevSubRef.current = finalSubs.join('|');
      setAppliedSubcategoryFilter(finalSubs);
      setSubcategoryFilter([]);
      setPendingRemoveSubcategorias([]);
    }

    if (segmentoFilter.length > 0 || pendingRemoveSegmentos.length > 0) {
      const combined = [...appliedSegmentoFilter, ...segmentoFilter];
      const uniqueSegs = combined.filter((item, index) => combined.indexOf(item) === index);
      const finalSegs = uniqueSegs.filter(s => !pendingRemoveSegmentos.includes(s));
      setAppliedSegmentoFilter(finalSegs);
      setSegmentoFilter([]);
      setPendingRemoveSegmentos([]);
    }

    // Aplicar filtros de precio y posición
    setAppliedPriceFromFilter(priceFromFilter);
    setAppliedPriceToFilter(priceToFilter);
    setAppliedMaxPageFilter(maxPageFilter);
    setAppliedMaxOrderFilter(maxOrderFilter);
    
    if (descuentoFilter !== appliedDescuentoFilter) {
      setAppliedDescuentoFilter(descuentoFilter);
    }
    
    // Forzar actualización inmediata de sharedFilters
    const filters: SharedFilters = {};
    
    if (terms.length > 0) {
      const nonNotTerms = terms.filter(term => operators[term] !== 'NOT');
      if (nonNotTerms.length > 0) {
        filters['global_search'] = nonNotTerms.join(' ');
      } else {
        filters['global_search'] = '';
      }
      filters['search_operators'] = operators;
    }
    
    // Agregar otros filtros aplicados actualmente
    // Combinar filtros pendientes con los ya aplicados + restar pendingRemoves
    // FIX 2026-05-30: sin esto, el fetch llamaba con Marca=Colgate aunque el
    // user click X de pill applied (pendingRemove se ignoraba en sharedFilters).
    const allMarcas = Array.from(new Set([...appliedMarcaFilter, ...marcaFilter]))
      .filter(m => !pendingRemoveMarcas.includes(m));
    if (allMarcas.length > 0) {
      const columnName = brandFilterColumn;
      filters[columnName] = allMarcas.join('|');
    }

    const allSitios = Array.from(new Set([...appliedSitioFilter, ...sitioFilter]))
      .filter(s => !pendingRemoveSitios.includes(s));
    if (allSitios.length > 0) {
      filters['sitio'] = allSitios.join('|');
    }

    const allCategorias = Array.from(new Set([...appliedCategoriaFilter, ...categoriaFilter]))
      .filter(c => !pendingRemoveCategorias.includes(c));
    if (allCategorias.length > 0) {
      const columnName = categoriaColumnName || 'Cat';
      filters[columnName] = allCategorias.join('|');
    }

    const allSubcategorias = Array.from(new Set([...appliedSubcategoryFilter, ...subcategoryFilter]))
      .filter(s => !pendingRemoveSubcategorias.includes(s));
    if (allSubcategorias.length > 0) {
      filters['subcategory'] = allSubcategorias.join('|');
    }

    const allSegmentos = Array.from(new Set([...appliedSegmentoFilter, ...segmentoFilter]))
      .filter(s => !pendingRemoveSegmentos.includes(s));
    if (allSegmentos.length > 0) {
      filters['segmento'] = allSegmentos.join('|');
    }

    // Para precios, usar el valor aplicado o el pendiente
    const finalPriceFrom = priceFromFilter || appliedPriceFromFilter;
    if (finalPriceFrom) {
      filters['price_from'] = finalPriceFrom;
    }
    
    const finalPriceTo = priceToFilter || appliedPriceToFilter;
    if (finalPriceTo) {
      filters['price_to'] = finalPriceTo;
    }
    
    const finalMaxPage = maxPageFilter || appliedMaxPageFilter;
    if (finalMaxPage) {
      filters['max_page'] = finalMaxPage;
    }
    
    const finalMaxOrder = maxOrderFilter || appliedMaxOrderFilter;
    if (finalMaxOrder) {
      filters['max_order'] = finalMaxOrder;
    }
    
    const finalDescuento = descuentoFilter !== 'all' ? descuentoFilter : appliedDescuentoFilter;
    if (finalDescuento && finalDescuento !== 'all') {
      filters['descuento_filter'] = finalDescuento;
    }
    
    
    setSharedFilters(filters);
    finishApply({ filterCount: Object.keys(filters).length });
  };

  // Handlers simples para los campos de precio (sin validación)
  const handlePriceFromChange = (value: string) => {
    setPriceFromFilter(value);
    // Limpiar errores cuando el usuario cambie los valores
    if (priceValidationErrors.length > 0) {
      setPriceValidationErrors([]);
    }
  };

  const handlePriceToChange = (value: string) => {
    setPriceToFilter(value);
    // Limpiar errores cuando el usuario cambie los valores
    if (priceValidationErrors.length > 0) {
      setPriceValidationErrors([]);
    }
  };

  // Validación de precios que se ejecuta al presionar "Filtrar"
  const validatePrices = (priceFrom: string, priceTo: string) => {
    const errors = [];
    
    if (priceFrom && parseFloat(priceFrom) < 0) {
      errors.push('El precio "desde" no puede ser negativo');
    }
    
    if (priceTo && parseFloat(priceTo) < 0) {
      errors.push('El precio "hasta" no puede ser negativo');
    }
    
    if (priceFrom && priceTo && parseFloat(priceTo) < parseFloat(priceFrom)) {
      errors.push('El precio "hasta" no puede ser menor que el precio "desde"');
    }
    
    return errors;
  };

  // Función para formatear precios como en las cards
  const formatPrice = (price: string | number): string => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '0';
    
    // Formatear con separador de miles (punto)
    return numPrice.toLocaleString('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    }).replace(/,/g, '.');
  };

  if (connectionStatus === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('errors.connection_title')}</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={checkConnection}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            {t('errors.retry_connection')}
          </button>
        </div>
      </div>
    );
  }


  const renderTabContent = () => {
    // No renderizar contenido de tabs hasta que las tablas estén cargadas
    // Esto evita que StatisticsPanel dispare API calls con tabla de localStorage
    // que luego se sobreescribe cuando loadTables termina
    if (!tablesLoaded || connectionStatus === 'connecting') {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <SkeletonStatCard key={i} />)}
          </div>
          <SkeletonDataTable />
        </div>
      );
    }

    if (activeTab === 'estadisticas') {
      if (!selectedTable) {
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <BarChart3 className="h-16 w-16 text-blue-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('statistics.title')}</h2>
              <p className="text-gray-600 mb-6">
                {t('dashboard.select_data_set')}
              </p>
            </div>
          </div>
        );
      }
      
      return (
        <div className="space-y-2">
          {/* Header con indicador de toma */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                {getTableDisplayName(selectedTable)}
              </h2>
              
              {/* Indicador conectado y selector de tomas */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  {t('dashboard.connected')}
                </div>
                
                {tables.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        loadTables();
                        loadTableSchema(selectedTable);
                      }}
                      className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                      title={t('dashboard.refresh_data') || 'Refrescar datos'}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    
                    <Table className="h-4 w-4 text-gray-500" />
                    <select
                      value={selectedTable}
                      onChange={(e) => {
                        setSelectedTable(e.target.value);
                      }}
                      className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-48"
                    >
                      <option value="">{t('dashboard.select_data_set')}</option>
                      {tables.map((toma, index) => (
                        <option key={index} value={toma.tableName}>
                          {getTableDropdownName(toma.tableName)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Panel de Filtros para Estadísticas */}
            <UnifiedFilterPanel
            selectedTable={selectedTable}
            activeTab={activeTab}
            marcaColumnName={marcaColumnName}
            sitioColumnName={sitioColumnName}
            categoriaColumnName={categoriaColumnName}
            globalFilter={globalFilter}
            marcaFilter={marcaFilter}
            sitioFilter={sitioFilter}
            categoriaFilter={categoriaFilter}
            appliedMarcaFilter={appliedMarcaFilter}
            appliedSitioFilter={appliedSitioFilter}
            appliedCategoriaFilter={appliedCategoriaFilter}
            pendingRemoveMarcas={pendingRemoveMarcas}
            setPendingRemoveMarcas={setPendingRemoveMarcas}
            pendingRemoveSitios={pendingRemoveSitios}
            setPendingRemoveSitios={setPendingRemoveSitios}
            pendingRemoveCategorias={pendingRemoveCategorias}
            setPendingRemoveCategorias={setPendingRemoveCategorias}
            priceFromFilter={priceFromFilter}
            priceToFilter={priceToFilter}
            appliedPriceFromFilter={appliedPriceFromFilter}
            appliedPriceToFilter={appliedPriceToFilter}
            maxPageFilter={maxPageFilter}
            maxOrderFilter={maxOrderFilter}
            appliedMaxPageFilter={appliedMaxPageFilter}
            appliedMaxOrderFilter={appliedMaxOrderFilter}
            descuentoFilter={descuentoFilter}
            appliedDescuentoFilter={appliedDescuentoFilter}
            searchTermOperators={searchTermOperators}
            appliedSearchTermOperators={appliedSearchTermOperators}
            marcaOptions={marcaOptions}
            sitioOptions={sitioOptions}
            categoriaOptions={categoriaOptions}
            marcaOptionsMeta={marcaOptionsMeta}
            sitioOptionsMeta={sitioOptionsMeta}
            categoriaOptionsMeta={categoriaOptionsMeta}
            subcategoryOptionsMeta={subcategoryOptionsMeta}
            setGlobalFilter={setGlobalFilter}
            setMarcaFilter={setMarcaFilter}
            setSitioFilter={setSitioFilter}
            setCategoriaFilter={setCategoriaFilter}
            setAppliedMarcaFilter={setAppliedMarcaFilter}
            setAppliedSitioFilter={setAppliedSitioFilter}
            setAppliedCategoriaFilter={setAppliedCategoriaFilter}
            setPriceFromFilter={setPriceFromFilter}
            setPriceToFilter={setPriceToFilter}
            setMaxPageFilter={setMaxPageFilter}
            setMaxOrderFilter={setMaxOrderFilter}
            setDescuentoFilter={setDescuentoFilter}
            setCategoriaOptions={setCategoriaOptions}
            handlePriceFromChange={handlePriceFromChange}
            handlePriceToChange={handlePriceToChange}
            handleSearchFilter={handleSearchFilter}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            activeFilterName={activeFilterName}
            activeFilterOriginal={activeFilterOriginal}
            loadingSavedFilters={loadingSavedFilters}
            facetsLoading={facetsLoading}
            resetTrigger={resetTrigger}
            onShowSaveFilterModal={() => setShowSaveFilterModal(true)}
            getCurrentFiltersForSaving={getCurrentFiltersForSaving}
            hasChanges={hasChanges}
            loadCategoriesApi={async (table) => {
              try {
                const url = categoriaColumnName.startsWith('__tag:')
                  ? `/api/tables/${table}/distinct-tag/${categoriaColumnName.replace('__tag:', '')}?withCounts=1`
                  : `/api/tables/${table}/distinct/${categoriaColumnName || 'Cat'}?withCounts=1`;
                const response = await api.get(url);
                const data = response.data;
                if (Array.isArray(data)) {
                  // shape vieja
                  return data.filter((item: any) => item && typeof item === 'string' && item.trim() !== '');
                }
                if (data && Array.isArray(data.values)) {
                  const counts: Record<string, number> = {};
                  const options: string[] = [];
                  const sorted = [...data.values].sort((a: any, b: any) => {
                    if (a.value === '(vacío)') return 1;
                    if (b.value === '(vacío)') return -1;
                    return String(a.value).localeCompare(String(b.value));
                  });
                  for (const v of sorted) {
                    if (v && typeof v.value === 'string') {
                      options.push(v.value);
                      counts[v.value] = Number(v.n) || 0;
                    }
                  }
                  setCategoriaOptionsMeta({ counts, total: Number(data.total) || options.length });
                  return options;
                }
                return [];
              } catch (error) {
                return [];
              }
            }}
            subcategoryFilterEnabled={subcategoryFilterEnabled}
            subcategoryFilter={subcategoryFilter}
            appliedSubcategoryFilter={appliedSubcategoryFilter}
            subcategoryOptions={subcategoryOptions}
            setSubcategoryFilter={setSubcategoryFilter}
            setAppliedSubcategoryFilter={setAppliedSubcategoryFilter}
            setSubcategoryOptions={setSubcategoryOptions}
            pendingRemoveSubcategorias={pendingRemoveSubcategorias}
            setPendingRemoveSubcategorias={setPendingRemoveSubcategorias}
            loadSubcategoriesApi={async (table) => {
              try {
                const response = await api.get(`/api/tables/${table}/distinct-subcategory?withCounts=1`);
                const data = response.data;
                if (data && Array.isArray(data.values)) {
                  const counts: Record<string, number> = {};
                  const options: string[] = [];
                  const sorted = [...data.values].sort((a: any, b: any) => {
                    if (a.value === '(vacío)') return 1;
                    if (b.value === '(vacío)') return -1;
                    return String(a.value).localeCompare(String(b.value));
                  });
                  for (const v of sorted) {
                    if (v && typeof v.value === 'string') {
                      options.push(v.value);
                      counts[v.value] = Number(v.n) || 0;
                    }
                  }
                  setSubcategoryOptionsMeta({ counts, total: Number(data.total) || options.length });
                  return options;
                }
                // shape vieja
                const arr = Array.isArray(data?.subcategories) ? data.subcategories : [];
                return arr.filter((s: any) => s && typeof s === 'string' && s.trim() !== '');
              } catch (error) {
                return [];
              }
            }}
            segmentoFilterEnabled={segmentoFilterEnabled}
            segmentoFilter={segmentoFilter}
            appliedSegmentoFilter={appliedSegmentoFilter}
            segmentoOptions={segmentoOptions}
            setSegmentoFilter={setSegmentoFilter}
            setAppliedSegmentoFilter={setAppliedSegmentoFilter}
            setSegmentoOptions={setSegmentoOptions}
            pendingRemoveSegmentos={pendingRemoveSegmentos}
            setPendingRemoveSegmentos={setPendingRemoveSegmentos}
            segmentoOptionsMeta={segmentoOptionsMeta}
            loadSegmentosApi={async (table) => {
              try {
                const response = await api.get(`/api/tables/${table}/distinct-segmento?withCounts=1`);
                const data = response.data;
                if (data && Array.isArray(data.values)) {
                  const counts: Record<string, number> = {};
                  const options: string[] = [];
                  const sorted = [...data.values].sort((a: any, b: any) => {
                    if (a.value === '(vacío)') return 1;
                    if (b.value === '(vacío)') return -1;
                    return String(a.value).localeCompare(String(b.value));
                  });
                  for (const v of sorted) {
                    if (v && typeof v.value === 'string') {
                      options.push(v.value);
                      counts[v.value] = Number(v.n) || 0;
                    }
                  }
                  setSegmentoOptionsMeta({ counts, total: Number(data.total) || options.length });
                  return options;
                }
                // shape vieja
                const arr = Array.isArray(data?.segmentos) ? data.segmentos : [];
                return arr.filter((s: any) => s && typeof s === 'string' && s.trim() !== '');
              } catch (error) {
                return [];
              }
            }}
            filterLayout={tableColumnConfig?.filterLayout}
            onClearAllFilters={() => {
              setGlobalFilter('');
              setMarcaFilter([]);
              setSitioFilter([]);
              setCategoriaFilter([]);
              setSubcategoryFilter([]);
              setSegmentoFilter([]);
              setAppliedMarcaFilter([]);
              setAppliedSitioFilter([]);
              setAppliedCategoriaFilter([]);
              setAppliedSubcategoryFilter([]);
              setAppliedSegmentoFilter([]);
              setPriceFromFilter('');
              setPriceToFilter('');
              setAppliedPriceFromFilter('');
              setAppliedPriceToFilter('');
              setMaxPageFilter('');
              setMaxOrderFilter('');
              setDescuentoFilter('all');
              setAppliedMaxPageFilter('');
              setAppliedMaxOrderFilter('');
              setAppliedDescuentoFilter('all');
              setActiveFilterName(null);
              setActiveFilterOriginal(null);
              setSearchTermOperators({});
              setAppliedSearchTermOperators({});
              setResetTrigger(prev => prev + 1);
              setSharedFilters({});
            }}
          />

          {/* Componente de estadísticas */}
          <StatisticsPanel
            tableName={selectedTable}
            filters={sharedFilters}
            tableDate={tables.find(t => t.tableName === selectedTable)?.fecha}
            activeFilterName={activeFilterName}
          />
        </div>
      );
    }

    if (activeTab === 'comparar') {
      return (
        <Comparar 
          comparisonProducts={comparisonProducts}
          onRemoveProduct={removeFromComparison}
          onClearAll={clearComparison}
          onLoadComparisonProducts={(products) => {
            
            // Crear IDs únicos para comparación
            const currentIds = comparisonProducts
              .map(p => p.comparisonId || p.id_catalogo || p.ID_CATALOGO || p.id)
              .filter(id => id)
              .sort()
              .join(',');
            
            const newIds = products
              .map(p => p.comparisonId || p.id_catalogo || p.ID_CATALOGO || p.id)
              .filter(id => id)
              .sort()
              .join(',');
            
            
            // Si los productos son exactamente los mismos, no hacer nada
            if (currentIds === newIds && products.length === comparisonProducts.length) {
              return;
            }
            
            // SOLUCIÓN: Primero limpiar el estado completamente, luego establecer los nuevos productos
            // Esto fuerza a React a re-renderizar completamente el componente
            setComparisonProducts([]); // Limpiar primero
            
            // Usar requestAnimationFrame para asegurar que el DOM se actualice
            requestAnimationFrame(() => {
              // Crear nuevos productos con referencias únicas
              const timestamp = Date.now();
              const newProducts = products.map((p, index) => ({
                ...p,
                comparisonId: p.comparisonId || p.id_catalogo || p.ID_CATALOGO || p.id || `product-${timestamp}-${index}`,
                _loadTimestamp: timestamp // Agregar timestamp temporal para forzar cambio
              }));
              
              setComparisonProducts(newProducts);
              
              // Guardar en localStorage también
              try {
                localStorage.setItem('comparisonProducts', JSON.stringify(newProducts));
              } catch (error) {
                console.error('Error saving to localStorage:', error);
              }
            });
          }}
          allProducts={allProducts}
          currentTableProducts={allProducts}
          currentTableInfo={selectedTable ? getCurrentTableInfo(selectedTable) : undefined}
          tables={tables}
          selectedTable={selectedTable}
          onTableChange={setSelectedTable}
          getTableDisplayName={getTableDisplayName}
          getTableDropdownName={getTableDropdownName}
          clientId={selectedClient?.clientId}
        />
      );
    }

    if (activeTab === 'posicion') {
      if (!selectedTable) {
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center py-12">
              <Grid3x3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('dashboard.select_data_set')}</h2>
              <p className="text-gray-600">
                {t('dashboard.select_data_set_description')}
              </p>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {/* Header con selector de tomas */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                {getTableDisplayName(selectedTable)}
              </h2>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  {t('dashboard.connected')}
                </div>
                
                {tables.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Table className="h-4 w-4 text-gray-500" />
                    <select
                      value={selectedTable}
                      onChange={(e) => {
                        setSelectedTable(e.target.value);
                      }}
                      className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-48"
                    >
                      <option value="">{t('dashboard.select_data_set')}</option>
                      {tables.map((toma, index) => (
                        <option key={index} value={toma.tableName}>
                          {getTableDropdownName(toma.tableName)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <PositionGrid tableName={selectedTable} />
          </div>
        </div>
      );
    }

    if (activeTab === 'catalogado') {
      return <Catalogado selectedTable={selectedTable} />;
    }

    if (activeTab === 'tendencias') {
      // PT-#58 — Cablear el opener del SaveFilterModal en Tendencias. Sin esto el modal
      // se monta pero nadie lo abre (Tendencias no usa UnifiedFilterPanel). El modal es
      // combinado (guardar + cargar + borrar), así que un solo botón habilita ambos flujos.
      return (
        <TrendsPanel
          tableName={selectedTable}
          onOpenSavedFilters={() => setShowSaveFilterModal(true)}
          loadingSavedFilters={loadingSavedFilters}
        />
      );
    }

    if (activeTab === 'exportar') {
      return (
        <ExportPanel
          tableName={selectedTable}
          brandFilterColumn={brandFilterColumn}
          categoryFilterColumn={categoriaColumnName || 'Cat'}
          onOpenSavedFilters={() => setShowSaveFilterModal(true)}
          loadingSavedFilters={loadingSavedFilters}
        />
      );
    }

    if (activeTab === 'alertas') {
      return <AlertsTab currentTableName={selectedTable} />;
    }

    // Pestaña PUBLICACIONES
    if (tables.length === 0) {
      // Si estamos cargando y no hay error aún, mostrar un estado de carga más sutil
      if (loading && !showError) {
        return (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">{t('loading')}...</p>
            </div>
          </div>
        );
      }
      
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center py-12">
            <Table className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('dashboard.no_data_available')}</h2>
            <p className="text-gray-600 mb-6">
              {t('dashboard.no_data_description')}
            </p>
            <button
              onClick={() => checkConnection()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {t('actions.refresh')}
            </button>
          </div>
        </div>
      );
    }

    if (!selectedTable) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="text-center py-12">
            <Table className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">{t('dashboard.select_data_set')}</h2>
            <p className="text-gray-600">
              {t('dashboard.select_data_set')}
            </p>
          </div>
        </div>
      );
    }

    // Renderizar la vista seleccionada
    return (
      <div className="space-y-2">
        {/* Header simplificado */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">
              {getTableDisplayName(selectedTable)}
            </h2>
            
            {/* Indicador conectado y selector de tomas al lado derecho */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                {t('dashboard.connected')}
              </div>
              
              {tables.length > 0 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      loadTables();
                      if (selectedTable) {
                        loadTableSchema(selectedTable);
                      }
                    }}
                    className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    title={t('dashboard.refresh_data') || 'Refrescar datos'}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                  
                  <Table className="h-4 w-4 text-gray-500" />
                  <select
                    value={selectedTable}
                    onChange={(e) => {
                      setSelectedTable(e.target.value);
                    }}
                    className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-48"
                  >
                    <option value="">{t('dashboard.select_data_set')}</option>
                    {tables.map((toma, index) => (
                      <option key={index} value={toma.tableName}>
                        {getTableDropdownName(toma.tableName)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sub-navegación para Publicaciones */}
        {activeTab === 'publicaciones' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-2">
            <div className="flex items-center space-x-1">
              <button
                onClick={() => {
                  // Incrementar key para forzar recarga de SKUs cuando volvemos de la pestaña SKUs
                  if (publicacionesSubTab === 'skus') {
                    setSkuRefreshKey(prev => prev + 1);
                  }
                  setPublicacionesSubTab('listado');
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  publicacionesSubTab === 'listado'
                    ? 'bg-pink-100 text-pink-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>{t('nav.publications_list')}</span>
                </div>
              </button>
              <button
                onClick={() => setPublicacionesSubTab('skus')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  publicacionesSubTab === 'skus'
                    ? 'bg-pink-100 text-pink-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Layers className="h-4 w-4" />
                  <span>{t('nav.product_identity')}</span>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* SKUs Management - dentro de Publicaciones */}
        {activeTab === 'publicaciones' && publicacionesSubTab === 'skus' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">{t('skus.identity_title')}</h3>
              <p className="text-sm text-gray-500 mt-1">{t('skus.identity_subtitle')}</p>
            </div>
            <SKUsManagement
              selectedTable={selectedTable}
              clientId={selectedClient?.clientId || ''}
            />
          </div>
        )}

        {/* Panel de Filtros - Solo para listado de publicaciones */}
        {activeTab === 'publicaciones' && publicacionesSubTab === 'listado' && (activeTab as string) !== 'comparar' && (
          <UnifiedFilterPanel
            selectedTable={selectedTable}
            activeTab={activeTab}
            marcaColumnName={marcaColumnName}
            sitioColumnName={sitioColumnName}
            categoriaColumnName={categoriaColumnName}
            globalFilter={globalFilter}
            marcaFilter={marcaFilter}
            sitioFilter={sitioFilter}
            categoriaFilter={categoriaFilter}
            appliedMarcaFilter={appliedMarcaFilter}
            appliedSitioFilter={appliedSitioFilter}
            appliedCategoriaFilter={appliedCategoriaFilter}
            pendingRemoveMarcas={pendingRemoveMarcas}
            setPendingRemoveMarcas={setPendingRemoveMarcas}
            pendingRemoveSitios={pendingRemoveSitios}
            setPendingRemoveSitios={setPendingRemoveSitios}
            pendingRemoveCategorias={pendingRemoveCategorias}
            setPendingRemoveCategorias={setPendingRemoveCategorias}
            priceFromFilter={priceFromFilter}
            priceToFilter={priceToFilter}
            appliedPriceFromFilter={appliedPriceFromFilter}
            appliedPriceToFilter={appliedPriceToFilter}
            maxPageFilter={maxPageFilter}
            maxOrderFilter={maxOrderFilter}
            appliedMaxPageFilter={appliedMaxPageFilter}
            appliedMaxOrderFilter={appliedMaxOrderFilter}
            descuentoFilter={descuentoFilter}
            appliedDescuentoFilter={appliedDescuentoFilter}
            searchTermOperators={searchTermOperators}
            appliedSearchTermOperators={appliedSearchTermOperators}
            marcaOptions={marcaOptions}
            sitioOptions={sitioOptions}
            categoriaOptions={categoriaOptions}
            marcaOptionsMeta={marcaOptionsMeta}
            sitioOptionsMeta={sitioOptionsMeta}
            categoriaOptionsMeta={categoriaOptionsMeta}
            subcategoryOptionsMeta={subcategoryOptionsMeta}
            setGlobalFilter={setGlobalFilter}
            setMarcaFilter={setMarcaFilter}
            setSitioFilter={setSitioFilter}
            setCategoriaFilter={setCategoriaFilter}
            setAppliedMarcaFilter={setAppliedMarcaFilter}
            setAppliedSitioFilter={setAppliedSitioFilter}
            setAppliedCategoriaFilter={setAppliedCategoriaFilter}
            setPriceFromFilter={setPriceFromFilter}
            setPriceToFilter={setPriceToFilter}
            setMaxPageFilter={setMaxPageFilter}
            setMaxOrderFilter={setMaxOrderFilter}
            setDescuentoFilter={setDescuentoFilter}
            setCategoriaOptions={setCategoriaOptions}
            handlePriceFromChange={handlePriceFromChange}
            handlePriceToChange={handlePriceToChange}
            handleSearchFilter={handleSearchFilter}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            activeFilterName={activeFilterName}
            activeFilterOriginal={activeFilterOriginal}
            loadingSavedFilters={loadingSavedFilters}
            facetsLoading={facetsLoading}
            resetTrigger={resetTrigger}
            onShowSaveFilterModal={() => setShowSaveFilterModal(true)}
            getCurrentFiltersForSaving={getCurrentFiltersForSaving}
            hasChanges={hasChanges}
            loadCategoriesApi={async (table) => {
              try {
                const url = categoriaColumnName.startsWith('__tag:')
                  ? `/api/tables/${table}/distinct-tag/${categoriaColumnName.replace('__tag:', '')}?withCounts=1`
                  : `/api/tables/${table}/distinct/${categoriaColumnName || 'Cat'}?withCounts=1`;
                const response = await api.get(url);
                const data = response.data;
                if (Array.isArray(data)) {
                  // shape vieja
                  return data.filter((item: any) => item && typeof item === 'string' && item.trim() !== '');
                }
                if (data && Array.isArray(data.values)) {
                  const counts: Record<string, number> = {};
                  const options: string[] = [];
                  const sorted = [...data.values].sort((a: any, b: any) => {
                    if (a.value === '(vacío)') return 1;
                    if (b.value === '(vacío)') return -1;
                    return String(a.value).localeCompare(String(b.value));
                  });
                  for (const v of sorted) {
                    if (v && typeof v.value === 'string') {
                      options.push(v.value);
                      counts[v.value] = Number(v.n) || 0;
                    }
                  }
                  setCategoriaOptionsMeta({ counts, total: Number(data.total) || options.length });
                  return options;
                }
                return [];
              } catch (error) {
                return [];
              }
            }}
            subcategoryFilterEnabled={subcategoryFilterEnabled}
            subcategoryFilter={subcategoryFilter}
            appliedSubcategoryFilter={appliedSubcategoryFilter}
            subcategoryOptions={subcategoryOptions}
            setSubcategoryFilter={setSubcategoryFilter}
            setAppliedSubcategoryFilter={setAppliedSubcategoryFilter}
            setSubcategoryOptions={setSubcategoryOptions}
            pendingRemoveSubcategorias={pendingRemoveSubcategorias}
            setPendingRemoveSubcategorias={setPendingRemoveSubcategorias}
            loadSubcategoriesApi={async (table) => {
              try {
                const response = await api.get(`/api/tables/${table}/distinct-subcategory?withCounts=1`);
                const data = response.data;
                if (data && Array.isArray(data.values)) {
                  const counts: Record<string, number> = {};
                  const options: string[] = [];
                  const sorted = [...data.values].sort((a: any, b: any) => {
                    if (a.value === '(vacío)') return 1;
                    if (b.value === '(vacío)') return -1;
                    return String(a.value).localeCompare(String(b.value));
                  });
                  for (const v of sorted) {
                    if (v && typeof v.value === 'string') {
                      options.push(v.value);
                      counts[v.value] = Number(v.n) || 0;
                    }
                  }
                  setSubcategoryOptionsMeta({ counts, total: Number(data.total) || options.length });
                  return options;
                }
                // shape vieja
                const arr = Array.isArray(data?.subcategories) ? data.subcategories : [];
                return arr.filter((s: any) => s && typeof s === 'string' && s.trim() !== '');
              } catch (error) {
                return [];
              }
            }}
            segmentoFilterEnabled={segmentoFilterEnabled}
            segmentoFilter={segmentoFilter}
            appliedSegmentoFilter={appliedSegmentoFilter}
            segmentoOptions={segmentoOptions}
            setSegmentoFilter={setSegmentoFilter}
            setAppliedSegmentoFilter={setAppliedSegmentoFilter}
            setSegmentoOptions={setSegmentoOptions}
            pendingRemoveSegmentos={pendingRemoveSegmentos}
            setPendingRemoveSegmentos={setPendingRemoveSegmentos}
            segmentoOptionsMeta={segmentoOptionsMeta}
            loadSegmentosApi={async (table) => {
              try {
                const response = await api.get(`/api/tables/${table}/distinct-segmento?withCounts=1`);
                const data = response.data;
                if (data && Array.isArray(data.values)) {
                  const counts: Record<string, number> = {};
                  const options: string[] = [];
                  const sorted = [...data.values].sort((a: any, b: any) => {
                    if (a.value === '(vacío)') return 1;
                    if (b.value === '(vacío)') return -1;
                    return String(a.value).localeCompare(String(b.value));
                  });
                  for (const v of sorted) {
                    if (v && typeof v.value === 'string') {
                      options.push(v.value);
                      counts[v.value] = Number(v.n) || 0;
                    }
                  }
                  setSegmentoOptionsMeta({ counts, total: Number(data.total) || options.length });
                  return options;
                }
                const arr = Array.isArray(data?.segmentos) ? data.segmentos : [];
                return arr.filter((s: any) => s && typeof s === 'string' && s.trim() !== '');
              } catch (error) {
                return [];
              }
            }}
            filterLayout={tableColumnConfig?.filterLayout}
            onClearAllFilters={() => {
              setGlobalFilter('');
              setMarcaFilter([]);
              setSitioFilter([]);
              setCategoriaFilter([]);
              setSubcategoryFilter([]);
              setSegmentoFilter([]);
              setAppliedMarcaFilter([]);
              setAppliedSitioFilter([]);
              setAppliedCategoriaFilter([]);
              setAppliedSubcategoryFilter([]);
              setAppliedSegmentoFilter([]);
              setPriceFromFilter('');
              setPriceToFilter('');
              setAppliedPriceFromFilter('');
              setAppliedPriceToFilter('');
              setMaxPageFilter('');
              setMaxOrderFilter('');
              setDescuentoFilter('all');
              setAppliedMaxPageFilter('');
              setAppliedMaxOrderFilter('');
              setAppliedDescuentoFilter('all');
              setActiveFilterName(null);
              setActiveFilterOriginal(null);
              setSearchTermOperators({});
              setAppliedSearchTermOperators({});
              setResetTrigger(prev => prev + 1);
              setSharedFilters({});
            }}
          />
        )}

        {/* Renderizar vista apropiada - Solo para listado */}
        {publicacionesSubTab === 'listado' && (
          viewMode === 'table' ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <DataTable
                key={`datatable-${selectedTable}-${skuRefreshKey}`}
                tableName={selectedTable}
                hideFilters={true}
                filters={sharedFilters}
                displayName={getTableDisplayName(selectedTable)}
                onAddToComparison={addToComparison}
                comparisonProducts={comparisonProducts}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                isTableTransitioning={isTableTransitioning}
                currentTableInfo={selectedTable ? getCurrentTableInfo(selectedTable) : undefined}
                enableTags={selectedClient?.enable_tags !== false}
                enableSku={true}
                clientId={selectedClient?.clientId}
              />
            </div>
          ) : (
            <GridView
              tableName={selectedTable}
              hideFilters={true}
              filters={(() => {
                return sharedFilters;
              })()}
              displayName={getTableDisplayName(selectedTable)}
              onAddToComparison={addToComparison}
              onRemoveFromComparison={removeFromComparison}
              comparisonProducts={comparisonProducts}
              viewMode={viewMode}
              onViewModeChange={handleViewModeChange}
              isTableTransitioning={isTableTransitioning}
              currentTableInfo={selectedTable ? getCurrentTableInfo(selectedTable) : undefined}
            />
          )
        )}
      </div>
    );
  };

  // Show SuperAdminPanel when in admin mode
  if (isInAdminMode || selectedClient?.clientId === 'admin-mode') {
    return (
      <ErrorBoundary>
        <SuperAdminPanel />
      </ErrorBoundary>
    );
  }

  
  return (
    <div className="min-h-screen">
      {/* Barra de colores superior animada */}
      <div className="h-1 bg-gradient-to-r from-cyan-400 via-pink-500 via-purple-600 via-yellow-400 to-green-400 animate-gradient-x bg-[length:400%_400%]"></div>
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo de Pulpou arriba a la izquierda */}
            <div className="flex items-center space-x-4">
              <img 
                src="/logo_pulpou.svg" 
                alt="Pulpou" 
                className="h-8 w-auto"
              />
              {selectedClient && selectedClient.clientLogo && (
                <div className="pl-4 border-l border-gray-300">
                  {selectedClient.clientLogo.startsWith('http') ? (
                    <img 
                      src={selectedClient.clientLogo} 
                      alt={selectedClient.clientName}
                      style={{ height: `${selectedClient.clientLogoHeight || 32}px` }}
                      className="w-auto object-contain"
                    />
                  ) : (
                    <div 
                      style={{ height: `${selectedClient.clientLogoHeight || 32}px` }}
                      className="w-auto flex items-center justify-center"
                      dangerouslySetInnerHTML={{ __html: selectedClient.clientLogo }}
                    />
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Menú de usuario */}
              <div className="relative user-dropdown">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center space-x-2 bg-white hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t('common.hello')} {(userData?.name || currentUser?.email?.split('@')[0] || 'Usuario')
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ')}
                    </p>
                  </div>
                  <MoreVertical className="w-4 h-4 text-gray-400" />
                </button>

                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {(() => {
                          if (userData?.name && userData?.lastname) {
                            return `${userData.name} ${userData.lastname}`;
                          }
                          return (userData?.name || currentUser?.email?.split('@')[0] || 'Usuario');
                        })()
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {userData?.user_type === 'superadmin' ? t('admin.superadmin') : userData?.user_type === 'admin' ? t('admin.users.admin') : t('admin.users.user_type')}
                      </p>
                    </div>
                    <div className="p-1">
                      {/* Platform Admin - Primera opción (admins y superadmins) */}
                      {(userData?.user_type === 'admin' || userData?.user_type === 'superadmin') && (
                        <button
                          onClick={() => {
                            // Go to admin panel
                            setIsInAdminMode(true);
                            setSelectedClient({
                              clientId: 'admin-mode',
                              clientName: 'Modo Administración',
                              clientLogo: undefined
                            });
                            // Save to sessionStorage (per-tab persistence)
                            setStoredSelectedClientRaw(JSON.stringify({
                              clientId: 'admin-mode',
                              clientName: 'Modo Administración',
                              clientLogo: undefined
                            }));
                            // Reload to admin panel
                            window.location.reload();
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md flex items-center space-x-2 transition-colors"
                        >
                          <Building2 className="w-4 h-4" />
                          <span>Platform Admin</span>
                        </button>
                      )}
                      
                      {/* Config Client - Segunda opción (solo cuando hay un cliente seleccionado) */}
                      {(userData?.user_type === 'admin' || userData?.user_type === 'superadmin') && selectedClient && selectedClient.clientId !== 'admin-mode' && (
                        <button
                          onClick={() => {
                            // Navegación SIN reload: cambiamos la URL con pushState +
                            // setSelectedClient(admin-mode). React re-renderiza, ClientsManagement
                            // lee el editClient del URL y abre ClientAdminPage directo.
                            // Sin flicker ni redirect intermedio al listado.
                            const targetUid = selectedClient.clientId;
                            const newUrl = `/?editClient=${encodeURIComponent(targetUid)}`;
                            window.history.pushState(null, '', newUrl);
                            // PT-#40 fix: desde la vista de datos ClientsManagement
                            // aún no está montado (su listener de popstate no existe),
                            // así que invalidamos el cache module-level para que el
                            // próximo mount relea ?editClient= del URL de forma
                            // determinista, sin depender del popstate (que se pierde).
                            resetEditClientCache();
                            setShowUserDropdown(false);
                            setIsInAdminMode(true);
                            setSelectedClient({
                              clientId: 'admin-mode',
                              clientName: 'Modo Administración',
                              clientLogo: undefined
                            });
                            // localStorage queda sincronizado por el useEffect de ClientContext.
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center space-x-2 transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          <span>Config Client</span>
                        </button>
                      )}
                      
                      {/* Profile - Tercera opción */}
                      <button
                        onClick={() => {/* TODO: Implementar perfil */}}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center space-x-2 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        <span>{t('common.profile')}</span>
                      </button>
                      
                      {/* Logout - Cuarta opción */}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-3 py-2 text-sm text-[#EC4899] hover:bg-pink-50 rounded-md flex items-center space-x-2 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>{t('common.logout')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Contenedor blanco principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <main className="p-6 space-y-2">
          {showError && error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {/* Tabs Navigation */}
          <div className="mb-6">
            {activeTab === 'admin' ? (
              // Empty div for layout consistency
              <div></div>
            ) : (
              <div className="border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <nav className="-mb-px flex space-x-5" aria-label="Tabs">
                    <button
                      onClick={() => {
                        // Si hay filtros pendientes, aplicarlos antes de cambiar de tab
                        if (hasPendingFilters()) {
                          applyAllPendingFilters();
                        }
                        setActiveTab('estadisticas');
                      }}
                      className={`${
                        activeTab === 'estadisticas'
                          ? 'border-pink-500 text-pink-600 bg-pink-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none`}
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>{t('nav.statistics').toUpperCase()}</span>
                    </button>
                    <button
                      onClick={() => {
                        if (hasPendingFilters()) {
                          applyAllPendingFilters();
                        }
                        setActiveTab('tendencias');
                      }}
                      className={`${
                        activeTab === 'tendencias'
                          ? 'border-pink-500 text-pink-600 bg-pink-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none`}
                    >
                      <TrendingUp className="h-4 w-4" />
                      <span>{t('nav.trends').toUpperCase()}</span>
                    </button>
                    <button
                      onClick={() => {
                        // Si hay filtros pendientes, aplicarlos antes de cambiar de tab
                        if (hasPendingFilters()) {
                          applyAllPendingFilters();
                        }
                        setActiveTab('publicaciones');
                        setPublicacionesSubTab('listado');
                      }}
                      className={`${
                        activeTab === 'publicaciones'
                          ? 'border-pink-500 text-pink-600 bg-pink-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none`}
                    >
                      <FileText className="h-4 w-4" />
                      <span>{t('nav.publications').toUpperCase()}</span>
                    </button>
                    <button
                      onClick={() => {
                        // Si hay filtros pendientes, aplicarlos antes de cambiar de tab
                        if (hasPendingFilters()) {
                          applyAllPendingFilters();
                        }
                        setActiveTab('comparar');
                      }}
                      className={`${
                        activeTab === 'comparar'
                          ? 'border-pink-500 text-pink-600 bg-pink-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none`}
                    >
                      <GitCompare className="h-4 w-4" />
                      <span>{t('nav.compare').toUpperCase()}</span>
                      {comparisonProducts.length > 0 && (
                        <span className="ml-2 bg-pink-100 text-pink-700 text-xs font-medium px-2 py-1 rounded-full min-w-[20px] h-5 flex items-center justify-center">
                          {comparisonProducts.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        // Si hay filtros pendientes, aplicarlos antes de cambiar de tab
                        if (hasPendingFilters()) {
                          applyAllPendingFilters();
                        }
                        setActiveTab('posicion');
                      }}
                      className={`${
                        activeTab === 'posicion'
                          ? 'border-pink-500 text-pink-600 bg-pink-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none`}
                    >
                      <Grid3x3 className="h-4 w-4" />
                      <span>{t('nav.position').toUpperCase()}</span>
                    </button>
                    {/* EXPORTAR — al final de la barra (después de POSICIÓN) */}
                    <button
                      onClick={() => {
                        if (hasPendingFilters()) {
                          applyAllPendingFilters();
                        }
                        setActiveTab('exportar');
                      }}
                      className={`${
                        activeTab === 'exportar'
                          ? 'border-pink-500 text-pink-600 bg-pink-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none`}
                    >
                      <Download className="h-4 w-4" />
                      <span>{t('nav.export', 'EXPORTAR').toUpperCase()}</span>
                    </button>
                    {/* TEMPORALMENTE OCULTO - Menú Alertas */}
                    {/* <button
                      onClick={() => {
                        if (hasPendingFilters()) {
                          applyAllPendingFilters();
                        }
                        setActiveTab('alertas');
                      }}
                      className={`${
                        activeTab === 'alertas'
                          ? 'border-pink-500 text-pink-600 bg-pink-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none`}
                    >
                      <Bell className="h-4 w-4" />
                      <span>{t('nav.alerts').toUpperCase()}</span>
                    </button> */}
                    {/* TEMPORALMENTE OCULTO - Menú Catalogado */}
                    {/* <button
                      onClick={() => {
                        // Si hay filtros pendientes, aplicarlos antes de cambiar de tab
                        if (hasPendingFilters()) {
                          applyAllPendingFilters();
                        }
                        setActiveTab('catalogado');
                      }}
                      className={`${
                        activeTab === 'catalogado'
                          ? 'border-pink-500 text-pink-600 bg-pink-50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none`}
                    >
                      <Tags className="h-4 w-4" />
                      <span>{t('nav.catalogado').toUpperCase()}</span>
                    </button> */}
                  </nav>
                  {/* Tab Config (solo superadmin) — abre ClientAdminPage del cliente seleccionado */}
                  {userData?.user_type === 'superadmin' && (
                    <button
                      onClick={() => {
                        const targetUid = selectedClient?.clientId;
                        if (!targetUid || targetUid === 'admin-mode') {
                          alert('Seleccioná un cliente primero');
                          return;
                        }
                        const newUrl = `/?editClient=${encodeURIComponent(targetUid)}`;
                        window.history.pushState(null, '', newUrl);
                        // PT-#40 fix: desde la vista de datos, ClientsManagement
                        // AÚN NO está montado, así que su listener de popstate no
                        // existe todavía y el dispatch se pierde (race). El fix
                        // determinista es invalidar el cache module-level ANTES del
                        // cambio de estado que monta ClientsManagement: al montar,
                        // su useState initializer relee ?editClient= del URL y abre
                        // ClientAdminPage directo (sin depender del popstate).
                        resetEditClientCache();
                        setIsInAdminMode(true);
                        setSelectedClient({
                          clientId: 'admin-mode',
                          clientName: 'Modo Administración',
                          clientLogo: undefined
                        });
                      }}
                      title="Administrar Cliente — abre la configuración del cliente seleccionado"
                      className="border-transparent text-amber-700 hover:text-amber-800 hover:border-amber-300 whitespace-nowrap py-2 px-4 border-b-2 font-semibold text-xs rounded-t-md transition-all duration-200 flex items-center space-x-2 focus:outline-none border-l border-l-gray-200 ml-2 pl-4"
                    >
                      <Settings className="h-4 w-4" />
                      <span>CONFIG</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

            {/* Tab Content */}
            {renderTabContent()}
          </main>
        </div>
      </div>

      {/* Modal de filtros guardados */}
      <SaveFilterModal
        isOpen={showSaveFilterModal}
        onClose={() => setShowSaveFilterModal(false)}
        currentFilters={currentFiltersForComparison}
        onSaveFilter={handleSaveFilter}
        onUpdateFilter={handleUpdateFilter}
        onDeleteFilter={deleteFilter}
        onLoadFilter={loadFiltersFromSaved}
        savedFilters={scopedSavedFilters}
        matchingFilter={matchingFilter}
        filterDifference={filterDifference}
        isApplyingFilter={isApplyingSavedFilter}
        hasActiveFilters={
          // PT-#58 — En Tendencias siempre hay ejes que guardar (nivel/agrupación/métrica
          // tienen valor por defecto), aunque no haya filtros de datos de Dashboard activos.
          // PT-#61 — Ídem EXPORTAR: su caja de filtros es independiente (bridge), así que el
          // hasActiveFilters de Dashboard no la ve; forzamos true para exponer "Guardar filtro".
          activeTab === 'tendencias' ||
          activeTab === 'exportar' ||
          globalFilter !== '' ||
          marcaFilter.length > 0 ||
          sitioFilter.length > 0 || 
          categoriaFilter.length > 0 || 
          appliedMarcaFilter.length > 0 ||
          appliedSitioFilter.length > 0 ||
          appliedCategoriaFilter.length > 0 ||
          priceFromFilter !== '' || 
          priceToFilter !== '' || 
          appliedPriceFromFilter !== '' ||
          appliedPriceToFilter !== '' ||
          maxPageFilter !== '' || 
          maxOrderFilter !== '' || 
          appliedMaxPageFilter !== '' ||
          appliedMaxOrderFilter !== '' ||
          descuentoFilter !== 'all' ||
          appliedDescuentoFilter !== 'all'
        }
      />
    </div>
  );
};

export default Dashboard; 
